import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Plus, Edit, Trash2, Upload, BarChart3, Loader2, Download, FileSpreadsheet, CheckCircle, XCircle, Users, FileText, Shield, AlertCircle, UserCheck, MessageSquare, MessageCircle, User, Flag, Ban, Search, Eye, MapPin, Globe, Megaphone, ToggleLeft, ToggleRight, Link as LinkIcon, Image } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { formatDate } from '../utils/helpers';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [leaders, setLeaders] = useState([]);
  const [issues, setIssues] = useState([]);
  const [roles, setRoles] = useState(['MP', 'MLA', 'Mayor', 'Councillor', 'Ward Member', 'Panchayat Leader']);
  const [deletingId, setDeletingId] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  
  // Modal states
  const [showAddLeaderModal, setShowAddLeaderModal] = useState(false);
  const [showEditLeaderModal, setShowEditLeaderModal] = useState(false);
  const [showLeaderBulkImportModal, setShowLeaderBulkImportModal] = useState(false);
  const [editingLeaderId, setEditingLeaderId] = useState(null);
  
  // Bulk import states
  const [leaderBulkFile, setLeaderBulkFile] = useState(null);
  const [leaderBulkImporting, setLeaderBulkImporting] = useState(false);
  const [leaderBulkImportResult, setLeaderBulkImportResult] = useState(null);
  
  // Form state for submission
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Form states for Leaders
  const [leaderForm, setLeaderForm] = useState({
    name: '',
    role: 'MLA',
    party: '',
    state: '',
    district: '',
    constituency: '',
    bio: '',
    status: 'active'
  });
  const [leaderImage, setLeaderImage] = useState(null);
  const [leaderImagePreview, setLeaderImagePreview] = useState(null);
  
  // Leader Form Geography Dropdowns
  const [leaderGeoStates, setLeaderGeoStates] = useState([]);
  const [leaderGeoDistricts, setLeaderGeoDistricts] = useState([]);
  const [leaderGeoConstituencies, setLeaderGeoConstituencies] = useState([]);
  const [leaderGeoLoading, setLeaderGeoLoading] = useState({ states: false, districts: false, constituencies: false });
  const [leaderSelectedStateId, setLeaderSelectedStateId] = useState('');
  const [leaderSelectedDistrictId, setLeaderSelectedDistrictId] = useState('');
  const [useLeaderDropdowns, setUseLeaderDropdowns] = useState(true); // Toggle between dropdown and manual input
  
  // Admin Issues Management States
  const [adminIssues, setAdminIssues] = useState([]);
  const [issuesLoading, setIssuesLoading] = useState(false);
  const [showAssignLeaderModal, setShowAssignLeaderModal] = useState(false);
  const [selectedIssueForAssign, setSelectedIssueForAssign] = useState(null);
  const [assignLeaderId, setAssignLeaderId] = useState('');
  const [updatingIssueId, setUpdatingIssueId] = useState(null);
  
  // Enhanced Issue Filters with sorting and pagination
  const [issueFilters, setIssueFilters] = useState({
    state: '',
    district: '',
    status: '',
    category: '',
    search: '',
    assigned: '',
    sortBy: 'recent'
  });
  const [issuePagination, setIssuePagination] = useState({
    page: 1,
    limit: 20,
    total_count: 0,
    total_pages: 0,
    has_next: false,
    has_prev: false
  });
  const [activeStatusTab, setActiveStatusTab] = useState('all');
  const [states, setStates] = useState({});
  const [districts, setDistricts] = useState([]);
  const [categories, setCategories] = useState([]);
  
  // Dashboard Stats
  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(true);
  
  // Users Management
  const [adminUsers, setAdminUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [userSearch, setUserSearch] = useState('');
  
  // Responses Management
  const [adminResponses, setAdminResponses] = useState([]);
  const [responsesLoading, setResponsesLoading] = useState(false);

  // Geography Management States
  const [geoStates, setGeoStates] = useState([]);
  const [geoDistricts, setGeoDistricts] = useState([]);
  const [geoConstituencies, setGeoConstituencies] = useState([]);
  const [geoStats, setGeoStats] = useState(null);
  const [geoLoading, setGeoLoading] = useState(false);
  const [selectedGeoState, setSelectedGeoState] = useState('');
  const [selectedGeoDistrict, setSelectedGeoDistrict] = useState('');
  const [editingGeoItem, setEditingGeoItem] = useState(null);
  const [showEditGeoModal, setShowEditGeoModal] = useState(false);
  const [geoEditForm, setGeoEditForm] = useState({ name: '', is_enabled: true });
  
  // Add/Delete Geography States
  const [showAddDistrictModal, setShowAddDistrictModal] = useState(false);
  const [showAddConstituencyModal, setShowAddConstituencyModal] = useState(false);
  const [showDeleteGeoModal, setShowDeleteGeoModal] = useState(false);
  const [deletingGeoItem, setDeletingGeoItem] = useState(null);
  const [addDistrictForm, setAddDistrictForm] = useState({ name: '', code: '' });
  const [addConstituencyForm, setAddConstituencyForm] = useState({ 
    name: '', 
    number: '', 
    reserved_category: '', 
    type: 'assembly' 
  });

  // Ads Management States
  const [ads, setAds] = useState([]);
  const [adsLoading, setAdsLoading] = useState(false);
  const [showAddAdModal, setShowAddAdModal] = useState(false);
  const [showEditAdModal, setShowEditAdModal] = useState(false);
  const [editingAd, setEditingAd] = useState(null);
  const [adIsActive, setAdIsActive] = useState(true);
  const [adImage, setAdImage] = useState(null);
  const [adImagePreview, setAdImagePreview] = useState(null);
  const [adSubmitting, setAdSubmitting] = useState(false);
  const [togglingAdId, setTogglingAdId] = useState(null);
  const [deletingAdId, setDeletingAdId] = useState(null);

  // Rooms Management States
  const [adminRooms, setAdminRooms] = useState([]);
  const [roomsLoading, setRoomsLoading] = useState(false);
  const [deletingRoomId, setDeletingRoomId] = useState(null);
  const [roomStatusFilter, setRoomStatusFilter] = useState('all');

  useEffect(() => {
    if (!user?.is_admin) return;
    fetchData();
    fetchLeaders();
    fetchAdminIssues();
    fetchRoles();
    fetchStats();
    fetchStates();
    fetchCategories();
    fetchAdminUsers();
    fetchAdminResponses();
    fetchGeoStates();
    fetchGeoStats();
    fetchAds();
  }, [user]);

  useEffect(() => {
    if (issueFilters.state) {
      fetchDistricts(issueFilters.state);
    } else {
      setDistricts([]);
    }
  }, [issueFilters.state]);

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchAdminIssues(1);
      setIssuePagination(prev => ({ ...prev, page: 1 }));
    }, 300);
    return () => clearTimeout(timer);
  }, [issueFilters]);

  // Handle status tab change
  const handleStatusTabChange = (tab) => {
    setActiveStatusTab(tab);
    if (tab === 'all') {
      setIssueFilters(prev => ({ ...prev, status: '' }));
    } else {
      setIssueFilters(prev => ({ ...prev, status: tab }));
    }
  };

  // Handle page change
  const handlePageChange = (newPage) => {
    fetchAdminIssues(newPage);
  };

  const fetchData = async () => {
    try {
      const issuesRes = await axios.get(`${API_URL}/api/issues`);
      setIssues(issuesRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  const fetchLeaders = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/leaders?status=`);
      setLeaders(response.data);
    } catch (error) {
      console.error('Error fetching leaders:', error);
    }
  };

  const fetchStats = async () => {
    setStatsLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/admin/stats`, { withCredentials: true });
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setStatsLoading(false);
    }
  };

  const fetchStates = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/states`);
      setStates(response.data);
    } catch (error) {
      console.error('Error fetching states:', error);
    }
  };

  const fetchDistricts = async (state) => {
    try {
      const response = await axios.get(`${API_URL}/api/geography/districts/${encodeURIComponent(state)}`);
      setDistricts(response.data.districts || []);
    } catch (error) {
      console.error('Error fetching districts:', error);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/issues/categories`);
      setCategories(response.data.categories || []);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchAdminIssues = async (page = 1) => {
    setIssuesLoading(true);
    try {
      const params = new URLSearchParams();
      if (issueFilters.state) params.append('state', issueFilters.state);
      if (issueFilters.district) params.append('district', issueFilters.district);
      if (issueFilters.status) params.append('status', issueFilters.status);
      if (issueFilters.category) params.append('category', issueFilters.category);
      if (issueFilters.search) params.append('search', issueFilters.search);
      if (issueFilters.assigned) params.append('assigned', issueFilters.assigned);
      if (issueFilters.sortBy) params.append('sort_by', issueFilters.sortBy);
      params.append('page', page);
      params.append('limit', issuePagination.limit);
      
      const response = await axios.get(`${API_URL}/api/admin/issues?${params.toString()}`, { withCredentials: true });
      
      // Handle new paginated response format
      if (response.data.issues) {
        setAdminIssues(response.data.issues);
        setIssuePagination(response.data.pagination);
      } else {
        // Fallback for old format
        setAdminIssues(response.data);
      }
    } catch (error) {
      console.error('Error fetching admin issues:', error);
      try {
        const fallbackRes = await axios.get(`${API_URL}/api/issues`);
        setAdminIssues(fallbackRes.data);
      } catch (e) {
        console.error('Fallback also failed:', e);
      }
    } finally {
      setIssuesLoading(false);
    }
  };

  const fetchAdminUsers = async () => {
    setUsersLoading(true);
    try {
      const params = userSearch ? `?search=${encodeURIComponent(userSearch)}` : '';
      const response = await axios.get(`${API_URL}/api/admin/users${params}`, { withCredentials: true });
      setAdminUsers(response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
    } finally {
      setUsersLoading(false);
    }
  };

  const fetchAdminResponses = async () => {
    setResponsesLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/admin/responses`, { withCredentials: true });
      setAdminResponses(response.data);
    } catch (error) {
      console.error('Error fetching responses:', error);
    } finally {
      setResponsesLoading(false);
    }
  };

  const fetchRoles = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/leaders/roles`);
      setRoles(response.data.roles || ['MP', 'MLA', 'Mayor', 'Councillor']);
    } catch (error) {
      console.error('Error fetching roles:', error);
    }
  };

  // ============= GEOGRAPHY MANAGEMENT FUNCTIONS =============
  const fetchGeoStates = async () => {
    setGeoLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/admin/geo/states`, { withCredentials: true });
      setGeoStates(response.data || []);
    } catch (error) {
      console.error('Error fetching geo states:', error);
    } finally {
      setGeoLoading(false);
    }
  };

  const fetchGeoStats = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/geo/stats`, { withCredentials: true });
      setGeoStats(response.data);
    } catch (error) {
      console.error('Error fetching geo stats:', error);
    }
  };

  const fetchGeoDistricts = async (stateId) => {
    if (!stateId) {
      setGeoDistricts([]);
      return;
    }
    try {
      const response = await axios.get(`${API_URL}/api/admin/geo/districts?state_id=${stateId}`, { withCredentials: true });
      setGeoDistricts(response.data || []);
    } catch (error) {
      console.error('Error fetching geo districts:', error);
      setGeoDistricts([]);
    }
  };

  const fetchGeoConstituencies = async (districtId) => {
    if (!districtId) {
      setGeoConstituencies([]);
      return;
    }
    try {
      const response = await axios.get(`${API_URL}/api/admin/geo/constituencies?district_id=${districtId}`, { withCredentials: true });
      setGeoConstituencies(response.data || []);
    } catch (error) {
      console.error('Error fetching geo constituencies:', error);
      setGeoConstituencies([]);
    }
  };

  const handleGeoStateSelect = (stateId) => {
    setSelectedGeoState(stateId);
    setSelectedGeoDistrict('');
    setGeoConstituencies([]);
    fetchGeoDistricts(stateId);
  };

  const handleGeoDistrictSelect = (districtId) => {
    setSelectedGeoDistrict(districtId);
    fetchGeoConstituencies(districtId);
  };

  // ============= LEADER FORM GEOGRAPHY FUNCTIONS =============
  const fetchLeaderGeoStates = async () => {
    setLeaderGeoLoading(prev => ({ ...prev, states: true }));
    try {
      const response = await axios.get(`${API_URL}/api/geo/states`);
      setLeaderGeoStates(response.data.states || []);
    } catch (error) {
      console.error('Error fetching leader geo states:', error);
      // Fallback to admin geo states
      try {
        const adminResponse = await axios.get(`${API_URL}/api/admin/geo/states`, { withCredentials: true });
        setLeaderGeoStates(adminResponse.data || []);
      } catch (e) {
        console.error('Fallback also failed:', e);
      }
    } finally {
      setLeaderGeoLoading(prev => ({ ...prev, states: false }));
    }
  };

  const fetchLeaderGeoDistricts = async (stateId) => {
    if (!stateId) {
      setLeaderGeoDistricts([]);
      return;
    }
    setLeaderGeoLoading(prev => ({ ...prev, districts: true }));
    try {
      const response = await axios.get(`${API_URL}/api/geo/states/${encodeURIComponent(stateId)}/districts`);
      setLeaderGeoDistricts(response.data.districts || []);
    } catch (error) {
      console.error('Error fetching leader districts:', error);
      setLeaderGeoDistricts([]);
    } finally {
      setLeaderGeoLoading(prev => ({ ...prev, districts: false }));
    }
  };

  const fetchLeaderGeoConstituencies = async (stateId, districtId = null) => {
    if (!stateId) {
      setLeaderGeoConstituencies([]);
      return;
    }
    setLeaderGeoLoading(prev => ({ ...prev, constituencies: true }));
    try {
      let response;
      if (districtId) {
        response = await axios.get(`${API_URL}/api/geo/districts/${encodeURIComponent(districtId)}/constituencies`);
      } else {
        response = await axios.get(`${API_URL}/api/geo/states/${encodeURIComponent(stateId)}/constituencies`);
      }
      setLeaderGeoConstituencies(response.data.constituencies || []);
    } catch (error) {
      console.error('Error fetching leader constituencies:', error);
      setLeaderGeoConstituencies([]);
    } finally {
      setLeaderGeoLoading(prev => ({ ...prev, constituencies: false }));
    }
  };

  const handleLeaderStateSelect = (stateId, stateName) => {
    setLeaderSelectedStateId(stateId);
    setLeaderSelectedDistrictId('');
    setLeaderGeoConstituencies([]);
    setLeaderForm(prev => ({ ...prev, state: stateName, district: '', constituency: '' }));
    fetchLeaderGeoDistricts(stateId);
    fetchLeaderGeoConstituencies(stateId);
  };

  const handleLeaderDistrictSelect = (districtId, districtName) => {
    setLeaderSelectedDistrictId(districtId);
    setLeaderForm(prev => ({ ...prev, district: districtName }));
    if (districtId) {
      fetchLeaderGeoConstituencies(leaderSelectedStateId, districtId);
    }
  };

  const handleLeaderConstituencySelect = (constituencyName) => {
    setLeaderForm(prev => ({ ...prev, constituency: constituencyName }));
  };

  const openEditGeoModal = (item, type) => {
    setEditingGeoItem({ ...item, type });
    setGeoEditForm({ name: item.name, is_enabled: item.is_enabled !== false });
    setShowEditGeoModal(true);
  };

  const handleUpdateGeoItem = async () => {
    if (!editingGeoItem) return;
    
    const { type } = editingGeoItem;
    let endpoint = '';
    let idField = '';
    
    if (type === 'state') {
      endpoint = `/api/admin/geo/states/${editingGeoItem.state_id}`;
      idField = 'state_id';
    } else if (type === 'district') {
      endpoint = `/api/admin/geo/districts/${editingGeoItem.district_id}`;
      idField = 'district_id';
    } else if (type === 'constituency') {
      endpoint = `/api/admin/geo/constituencies/${editingGeoItem.constituency_id}`;
      idField = 'constituency_id';
    }
    
    try {
      await axios.put(`${API_URL}${endpoint}`, geoEditForm, { withCredentials: true });
      toast.success(`${type.charAt(0).toUpperCase() + type.slice(1)} updated successfully`);
      setShowEditGeoModal(false);
      
      // Refresh the relevant list
      if (type === 'state') {
        fetchGeoStates();
      } else if (type === 'district') {
        fetchGeoDistricts(selectedGeoState);
      } else if (type === 'constituency') {
        fetchGeoConstituencies(selectedGeoDistrict);
      }
      fetchGeoStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update');
    }
  };

  // Add District
  const handleAddDistrict = async () => {
    if (!addDistrictForm.name.trim()) {
      toast.error('Please enter district name');
      return;
    }
    if (!selectedGeoState) {
      toast.error('Please select a state first');
      return;
    }
    
    try {
      await axios.post(`${API_URL}/api/admin/geo/districts`, {
        state_id: selectedGeoState,
        name: addDistrictForm.name.trim(),
        code: addDistrictForm.code.trim() || addDistrictForm.name.trim().substring(0, 3).toUpperCase()
      }, { withCredentials: true });
      
      toast.success('District added successfully');
      setShowAddDistrictModal(false);
      setAddDistrictForm({ name: '', code: '' });
      fetchGeoDistricts(selectedGeoState);
      fetchGeoStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add district');
    }
  };

  // Add Constituency
  const handleAddConstituency = async () => {
    if (!addConstituencyForm.name.trim()) {
      toast.error('Please enter constituency name');
      return;
    }
    if (!selectedGeoDistrict) {
      toast.error('Please select a district first');
      return;
    }
    
    try {
      await axios.post(`${API_URL}/api/admin/geo/constituencies`, {
        district_id: selectedGeoDistrict,
        state_id: selectedGeoState,
        name: addConstituencyForm.name.trim(),
        number: addConstituencyForm.number ? parseInt(addConstituencyForm.number) : null,
        reserved_category: addConstituencyForm.reserved_category || null,
        type: addConstituencyForm.type || 'assembly'
      }, { withCredentials: true });
      
      toast.success('Constituency added successfully');
      setShowAddConstituencyModal(false);
      setAddConstituencyForm({ name: '', number: '', reserved_category: '', type: 'assembly' });
      fetchGeoConstituencies(selectedGeoDistrict);
      fetchGeoStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add constituency');
    }
  };

  // Delete Geography Item
  const openDeleteGeoModal = (item, type) => {
    setDeletingGeoItem({ ...item, type });
    setShowDeleteGeoModal(true);
  };

  const handleDeleteGeoItem = async () => {
    if (!deletingGeoItem) return;
    
    const { type } = deletingGeoItem;
    let endpoint = '';
    
    if (type === 'district') {
      endpoint = `/api/admin/geo/districts/${deletingGeoItem.district_id}`;
    } else if (type === 'constituency') {
      endpoint = `/api/admin/geo/constituencies/${deletingGeoItem.constituency_id}`;
    } else {
      toast.error('States cannot be deleted');
      return;
    }
    
    try {
      await axios.delete(`${API_URL}${endpoint}`, { withCredentials: true });
      toast.success(`${type.charAt(0).toUpperCase() + type.slice(1)} deleted successfully`);
      setShowDeleteGeoModal(false);
      setDeletingGeoItem(null);
      
      // Refresh the relevant list
      if (type === 'district') {
        fetchGeoDistricts(selectedGeoState);
        setSelectedGeoDistrict('');
        setGeoConstituencies([]);
      } else if (type === 'constituency') {
        fetchGeoConstituencies(selectedGeoDistrict);
      }
      fetchGeoStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete');
    }
  };

  // ============= ADS MANAGEMENT FUNCTIONS =============
  
  const fetchAds = async () => {
    setAdsLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/admin/ads`, { withCredentials: true });
      setAds(response.data.ads || []);
    } catch (error) {
      console.error('Error fetching ads:', error);
    } finally {
      setAdsLoading(false);
    }
  };

  // Fetch Rooms for Admin
  const fetchAdminRooms = async () => {
    setRoomsLoading(true);
    try {
      // Fetch all rooms - use 'all' to get all statuses, or specific status filter
      const statusQuery = roomStatusFilter === 'all' ? 'status=all' : `status=${roomStatusFilter}`;
      const response = await axios.get(`${API_URL}/api/rooms?room_type=public&limit=100&${statusQuery}`, { withCredentials: true });
      setAdminRooms(response.data.rooms || []);
    } catch (error) {
      console.error('Error fetching rooms:', error);
      toast.error('Failed to fetch rooms');
    } finally {
      setRoomsLoading(false);
    }
  };

  // Delete Room (Admin)
  const handleDeleteRoom = async (roomId) => {
    setDeletingRoomId(roomId);
    try {
      await axios.delete(`${API_URL}/api/rooms/${roomId}`, { withCredentials: true });
      toast.success('Room deleted successfully');
      setDeleteConfirm(null);
      fetchAdminRooms();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete room');
    } finally {
      setDeletingRoomId(null);
    }
  };

  const handleAdImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setAdImage(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setAdImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const resetAdForm = () => {
    // Reset uncontrolled inputs
    const titleEl = document.getElementById('ad-title');
    const descEl = document.getElementById('ad-description');
    const linkEl = document.getElementById('ad-link');
    if (titleEl) titleEl.value = '';
    if (descEl) descEl.value = '';
    if (linkEl) linkEl.value = '';
    setAdIsActive(true);
    setAdImage(null);
    setAdImagePreview(null);
  };

  const handleCreateAd = async (e) => {
    e.preventDefault();
    
    // Get values from form elements directly
    const titleEl = document.getElementById('ad-title');
    const descEl = document.getElementById('ad-description');
    const linkEl = document.getElementById('ad-link');
    
    const title = titleEl?.value || '';
    const description = descEl?.value || '';
    const link = linkEl?.value || '';
    
    if (!title.trim()) {
      toast.error('Title is required');
      return;
    }

    setAdSubmitting(true);
    try {
      const formData = new FormData();
      formData.append('title', title);
      formData.append('description', description);
      formData.append('link', link);
      formData.append('is_active', adIsActive);
      if (adImage) {
        formData.append('image', adImage);
      }

      await axios.post(`${API_URL}/api/admin/ads`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      toast.success('Ad created successfully');
      setShowAddAdModal(false);
      resetAdForm();
      fetchAds();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create ad');
    } finally {
      setAdSubmitting(false);
    }
  };

  const handleToggleAd = async (adId) => {
    setTogglingAdId(adId);
    try {
      const response = await axios.patch(`${API_URL}/api/admin/ads/${adId}/toggle`, {}, { withCredentials: true });
      setAds(prev => prev.map(ad => 
        ad.ad_id === adId ? { ...ad, is_active: response.data.is_active } : ad
      ));
      toast.success(`Ad ${response.data.is_active ? 'activated' : 'deactivated'}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to toggle ad');
    } finally {
      setTogglingAdId(null);
    }
  };

  const handleDeleteAd = async (adId) => {
    if (!window.confirm('Are you sure you want to delete this ad?')) return;
    
    setDeletingAdId(adId);
    try {
      await axios.delete(`${API_URL}/api/admin/ads/${adId}`, { withCredentials: true });
      setAds(prev => prev.filter(ad => ad.ad_id !== adId));
      toast.success('Ad deleted successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete ad');
    } finally {
      setDeletingAdId(null);
    }
  };

  const openEditAdModal = (ad) => {
    setEditingAd(ad);
    setAdIsActive(ad.is_active);
    setAdImagePreview(ad.image_url ? (ad.image_url.startsWith('http') ? ad.image_url : `${API_URL}${ad.image_url}`) : null);
    setShowEditAdModal(true);
  };

  const handleEditAd = async (e) => {
    e.preventDefault();
    
    const titleEl = document.getElementById('edit-ad-title');
    const descEl = document.getElementById('edit-ad-description');
    const linkEl = document.getElementById('edit-ad-link');
    
    const title = titleEl?.value || '';
    const description = descEl?.value || '';
    const link = linkEl?.value || '';
    
    if (!title.trim()) {
      toast.error('Title is required');
      return;
    }

    setAdSubmitting(true);
    try {
      const formData = new FormData();
      formData.append('title', title);
      formData.append('description', description);
      formData.append('link', link);
      formData.append('is_active', adIsActive);
      if (adImage) {
        formData.append('image', adImage);
      }

      await axios.put(`${API_URL}/api/admin/ads/${editingAd.ad_id}`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      toast.success('Ad updated successfully');
      setShowEditAdModal(false);
      setEditingAd(null);
      resetAdForm();
      fetchAds();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update ad');
    } finally {
      setAdSubmitting(false);
    }
  };

  // ============= LEADER MANAGEMENT FUNCTIONS =============
  
  const resetLeaderForm = useCallback(() => {
    setLeaderForm({
      name: '',
      role: 'MLA',
      party: '',
      state: '',
      district: '',
      constituency: '',
      bio: '',
      early_life_education: '',
      achievements: '',
      controversies: '',
      birth_date: '',
      position: '',
      assumed_office: '',
      status: 'active'
    });
    setLeaderImage(null);
    setLeaderImagePreview(null);
    // Reset geography dropdowns
    setLeaderSelectedStateId('');
    setLeaderSelectedDistrictId('');
    setLeaderGeoDistricts([]);
    setLeaderGeoConstituencies([]);
  }, []);

  const openAddLeaderModal = () => {
    resetLeaderForm();
    fetchLeaderGeoStates(); // Load states for dropdown
    setShowAddLeaderModal(true);
  };

  const openEditLeaderModal = (leader) => {
    setEditingLeaderId(leader.leader_id);
    setLeaderForm({
      name: leader.name || '',
      role: leader.role || 'MLA',
      party: leader.party || '',
      state: leader.state || '',
      district: leader.district || '',
      constituency: leader.constituency || '',
      bio: leader.bio || '',
      early_life_education: leader.early_life_education || '',
      achievements: leader.achievements || '',
      controversies: leader.controversies || '',
      birth_date: leader.birth_date || '',
      position: leader.position || '',
      assumed_office: leader.assumed_office || '',
      status: leader.status || 'active'
    });
    setLeaderImage(null);
    // Convert photo URL to full URL for preview
    if (leader.photo_url) {
      const fullUrl = leader.photo_url.startsWith('http') ? leader.photo_url : `${API_URL}${leader.photo_url}`;
      setLeaderImagePreview(fullUrl);
    } else {
      setLeaderImagePreview(null);
    }
    setShowEditLeaderModal(true);
  };

  const closeAddLeaderModal = () => {
    setShowAddLeaderModal(false);
    resetLeaderForm();
  };

  const closeEditLeaderModal = () => {
    setShowEditLeaderModal(false);
    setEditingLeaderId(null);
    resetLeaderForm();
  };

  const handleLeaderImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setLeaderImage(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setLeaderImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleCreateLeader = async (e) => {
    e.preventDefault();
    
    // Normalize values (trim whitespace)
    const normalizedForm = {
      name: leaderForm.name?.trim() || '',
      role: leaderForm.role?.trim() || '',
      party: leaderForm.party?.trim() || '',
      state: leaderForm.state?.trim() || '',
      district: leaderForm.district?.trim() || '',
      constituency: leaderForm.constituency?.trim() || '',
      bio: leaderForm.bio?.trim() || '',
      early_life_education: leaderForm.early_life_education?.trim() || '',
      achievements: leaderForm.achievements?.trim() || '',
      controversies: leaderForm.controversies?.trim() || '',
      birth_date: leaderForm.birth_date?.trim() || '',
      position: leaderForm.position?.trim() || '',
      assumed_office: leaderForm.assumed_office?.trim() || '',
      status: leaderForm.status || 'active'
    };
    
    if (!normalizedForm.name || !normalizedForm.role || !normalizedForm.party || !normalizedForm.state || !normalizedForm.constituency) {
      toast.error('Please fill all required fields');
      return;
    }

    setIsSubmitting(true);
    const formData = new FormData();
    formData.append('name', normalizedForm.name);
    formData.append('role', normalizedForm.role);
    formData.append('party', normalizedForm.party);
    formData.append('state', normalizedForm.state);
    formData.append('constituency', normalizedForm.constituency);
    formData.append('status', normalizedForm.status);
    if (normalizedForm.district) formData.append('district', normalizedForm.district);
    if (normalizedForm.bio) formData.append('bio', normalizedForm.bio);
    if (normalizedForm.early_life_education) formData.append('early_life_education', normalizedForm.early_life_education);
    if (normalizedForm.achievements) formData.append('achievements', normalizedForm.achievements);
    if (normalizedForm.controversies) formData.append('controversies', normalizedForm.controversies);
    if (normalizedForm.birth_date) formData.append('birth_date', normalizedForm.birth_date);
    if (normalizedForm.position) formData.append('position', normalizedForm.position);
    if (normalizedForm.assumed_office) formData.append('assumed_office', normalizedForm.assumed_office);
    if (leaderImage) formData.append('photo', leaderImage);

    try {
      await axios.post(`${API_URL}/api/admin/leaders`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        withCredentials: true
      });
      toast.success('Leader added successfully');
      closeAddLeaderModal();
      fetchLeaders();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add leader');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpdateLeader = async (e) => {
    e.preventDefault();
    if (!editingLeaderId) return;

    setIsSubmitting(true);
    const formData = new FormData();
    if (leaderForm.name) formData.append('name', leaderForm.name);
    if (leaderForm.role) formData.append('role', leaderForm.role);
    if (leaderForm.party) formData.append('party', leaderForm.party);
    if (leaderForm.state) formData.append('state', leaderForm.state);
    if (leaderForm.district !== undefined) formData.append('district', leaderForm.district);
    if (leaderForm.constituency) formData.append('constituency', leaderForm.constituency);
    formData.append('bio', leaderForm.bio || '');
    formData.append('early_life_education', leaderForm.early_life_education || '');
    formData.append('achievements', leaderForm.achievements || '');
    formData.append('controversies', leaderForm.controversies || '');
    if (leaderForm.birth_date) formData.append('birth_date', leaderForm.birth_date);
    if (leaderForm.position) formData.append('position', leaderForm.position);
    if (leaderForm.assumed_office) formData.append('assumed_office', leaderForm.assumed_office);
    if (leaderForm.status) formData.append('status', leaderForm.status);
    if (leaderImage) formData.append('photo', leaderImage);

    try {
      await axios.put(`${API_URL}/api/admin/leaders/${editingLeaderId}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        withCredentials: true
      });
      toast.success('Leader updated successfully');
      closeEditLeaderModal();
      fetchLeaders();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update leader');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteLeader = async (id) => {
    setDeletingId(id);
    try {
      await axios.delete(`${API_URL}/api/admin/leaders/${id}`, { withCredentials: true });
      toast.success('Leader deleted');
      setDeleteConfirm(null);
      fetchLeaders();
    } catch (error) {
      console.error('Delete error:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete leader');
    } finally {
      setDeletingId(null);
    }
  };

  const confirmDeleteLeader = (leader) => {
    setDeleteConfirm({ type: 'leader', id: leader.leader_id, name: leader.name });
  };

  // Leader Bulk Import Functions
  const handleDownloadLeaderTemplate = () => {
    window.open(`${API_URL}/api/admin/leaders/template`, '_blank');
  };

  const handleLeaderBulkFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const validExtensions = ['.csv', '.xlsx', '.xls'];
      const hasValidExtension = validExtensions.some(ext => file.name.toLowerCase().endsWith(ext));
      
      if (!hasValidExtension) {
        toast.error('Please upload a CSV or Excel file');
        return;
      }
      setLeaderBulkFile(file);
      setLeaderBulkImportResult(null);
    }
  };

  const handleLeaderBulkImport = async () => {
    if (!leaderBulkFile) {
      toast.error('Please select a file first');
      return;
    }

    setLeaderBulkImporting(true);
    setLeaderBulkImportResult(null);

    try {
      const formData = new FormData();
      formData.append('file', leaderBulkFile);

      const response = await axios.post(
        `${API_URL}/api/admin/leaders/bulk-import`,
        formData,
        {
          headers: { 'Content-Type': 'multipart/form-data' },
          withCredentials: true
        }
      );

      setLeaderBulkImportResult(response.data);
      
      if (response.data.success > 0) {
        toast.success(`Successfully imported ${response.data.success} leaders`);
        fetchLeaders();
      }
      
      if (response.data.failed > 0) {
        toast.error(`Failed to import ${response.data.failed} entries`);
      }
      
      if (response.data.marked_former > 0) {
        toast.info(`${response.data.marked_former} previous leaders marked as former`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to import leaders');
      setLeaderBulkImportResult({ error: error.response?.data?.detail || 'Import failed' });
    } finally {
      setLeaderBulkImporting(false);
    }
  };

  const closeLeaderBulkImportModal = () => {
    setShowLeaderBulkImportModal(false);
    setLeaderBulkFile(null);
    setLeaderBulkImportResult(null);
  };

  const getRoleBadgeColor = (role) => {
    const colors = {
      'MP': 'bg-purple-100 text-purple-700',
      'MLA': 'bg-blue-100 text-blue-700',
      'Mayor': 'bg-orange-100 text-orange-700',
      'Councillor': 'bg-teal-100 text-teal-700',
    };
    return colors[role] || 'bg-gray-100 text-gray-700';
  };

  // ============= ADMIN ISSUE MANAGEMENT FUNCTIONS =============
  
  const handleUpdateIssueStatus = async (issueId, newStatus) => {
    setUpdatingIssueId(issueId);
    try {
      await axios.patch(
        `${API_URL}/api/admin/issues/${issueId}/status?status=${encodeURIComponent(newStatus)}`,
        {},
        { withCredentials: true }
      );
      toast.success(`Status updated to ${newStatus}`);
      fetchAdminIssues();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update status');
    } finally {
      setUpdatingIssueId(null);
    }
  };

  const handleToggleVerified = async (issueId) => {
    setUpdatingIssueId(issueId);
    try {
      const response = await axios.patch(
        `${API_URL}/api/admin/issues/${issueId}/verify`,
        {},
        { withCredentials: true }
      );
      toast.success(response.data.message);
      fetchAdminIssues();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to toggle verification');
    } finally {
      setUpdatingIssueId(null);
    }
  };

  const openAssignLeaderModal = (issue) => {
    setSelectedIssueForAssign(issue);
    setAssignLeaderId(issue.primary_leader_id || issue.politician_id || '');
    setShowAssignLeaderModal(true);
  };

  const handleAssignLeader = async () => {
    if (!selectedIssueForAssign || !assignLeaderId) {
      toast.error('Please select a leader');
      return;
    }
    
    setUpdatingIssueId(selectedIssueForAssign.issue_id);
    try {
      const formData = new FormData();
      formData.append('leader_id', assignLeaderId);
      
      await axios.patch(
        `${API_URL}/api/admin/issues/${selectedIssueForAssign.issue_id}/assign`,
        formData,
        { 
          headers: { 'Content-Type': 'multipart/form-data' },
          withCredentials: true 
        }
      );
      toast.success('Leader assigned successfully');
      setShowAssignLeaderModal(false);
      setSelectedIssueForAssign(null);
      fetchAdminIssues();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to assign leader');
    } finally {
      setUpdatingIssueId(null);
    }
  };

  const handleDeleteIssue = async (issueId) => {
    setDeletingId(issueId);
    try {
      await axios.delete(`${API_URL}/api/admin/issues/${issueId}`, { withCredentials: true });
      toast.success('Issue deleted');
      setDeleteConfirm(null);
      fetchAdminIssues();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete issue');
    } finally {
      setDeletingId(null);
    }
  };

  const confirmDeleteIssue = (issue) => {
    setDeleteConfirm({ type: 'issue', id: issue.issue_id, name: issue.title });
  };

  const clearIssueFilters = () => {
    setIssueFilters({
      state: '',
      district: '',
      status: '',
      category: '',
      search: '',
      assigned: '',
      sortBy: 'recent'
    });
    setIssuePagination(prev => ({ ...prev, page: 1 }));
  };

  // ============= USER MANAGEMENT FUNCTIONS =============
  
  const handleFlagUser = async (userId, userName) => {
    try {
      const formData = new FormData();
      formData.append('reason', 'Flagged by admin');
      await axios.post(`${API_URL}/api/admin/users/${userId}/flag`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        withCredentials: true
      });
      toast.success(`User ${userName} has been flagged`);
      fetchAdminUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to flag user');
    }
  };

  const handleUnflagUser = async (userId, userName) => {
    try {
      await axios.post(`${API_URL}/api/admin/users/${userId}/unflag`, {}, { withCredentials: true });
      toast.success(`Flag removed from ${userName}`);
      fetchAdminUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to unflag user');
    }
  };

  const handleUnbanUser = async (userId, userName) => {
    try {
      await axios.post(`${API_URL}/api/admin/users/${userId}/unban`, {}, { withCredentials: true });
      toast.success(`${userName} has been unbanned`);
      fetchAdminUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to unban user');
    }
  };

  const handleBanUser = async (userId, userName) => {
    if (!window.confirm(`Are you sure you want to ban ${userName}? They will be unable to log in or participate.`)) return;
    
    try {
      const formData = new FormData();
      formData.append('reason', 'Banned by admin');
      await axios.post(`${API_URL}/api/admin/users/${userId}/ban`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        withCredentials: true
      });
      toast.success(`${userName} has been banned`);
      fetchAdminUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to ban user');
    }
  };

  const handleViewUser = (userId) => {
    navigate(`/profile/${userId}`);
  };

  // ============= RESPONSE MODERATION FUNCTIONS =============
  
  const handleDeleteResponse = async (responseId) => {
    setDeletingId(responseId);
    try {
      await axios.delete(`${API_URL}/api/admin/leader-responses/${responseId}`, { withCredentials: true });
      toast.success('Response deleted');
      setDeleteConfirm(null);
      fetchAdminResponses();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete response');
    } finally {
      setDeletingId(null);
    }
  };

  const confirmDeleteResponse = (response) => {
    setDeleteConfirm({ type: 'response', id: response.response_id, name: `Response by ${response.leader_name}` });
  };

  const getStatusBadgeColor = (status) => {
    switch (status) {
      case 'Resolved': return 'bg-green-100 text-green-700';
      case 'In Progress': return 'bg-blue-100 text-blue-700';
      case 'Pending': return 'bg-yellow-100 text-yellow-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const getCategoryIcon = (category) => {
    const icons = {
      'Road': '🛣️', 'Electricity': '⚡', 'Water': '💧', 'Healthcare': '🏥',
      'Education': '📚', 'Public Safety': '🛡️', 'Corruption': '⚠️',
      'Government Scheme': '🏛️', 'Sanitation': '🧹', 'Environment': '🌳',
      'Transport': '🚌', 'Housing': '🏠'
    };
    return icons[category] || '📋';
  };

  const getImageUrl = (url) => {
    if (!url) return '/logo.png';
    return url.startsWith('http') ? url : `${API_URL}${url}`;
  };

  if (!user?.is_admin) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <p className="text-gray-600">Access denied. Admin privileges required.</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-between mb-8">
          <h1 className="text-4xl font-bold text-gray-900">Admin Dashboard</h1>
          <Button
            onClick={() => navigate('/admin/analytics')}
            variant="outline"
            className="border-blue-600 text-blue-600 hover:bg-blue-50"
          >
            <BarChart3 className="mr-2 h-4 w-4" />
            Analytics
          </Button>
        </div>

        {/* Dashboard Overview Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 mb-8">
          {statsLoading ? (
            <div className="col-span-full flex justify-center py-4">
              <Loader2 className="h-6 w-6 animate-spin text-blue-600" />
            </div>
          ) : stats && (
            <>
              <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <FileText className="h-5 w-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-gray-900">{stats.issues?.total || 0}</p>
                    <p className="text-xs text-gray-500">Total Issues</p>
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-yellow-100 rounded-lg">
                    <AlertCircle className="h-5 w-5 text-yellow-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-yellow-600">{stats.issues?.pending || 0}</p>
                    <p className="text-xs text-gray-500">Pending</p>
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-green-100 rounded-lg">
                    <CheckCircle className="h-5 w-5 text-green-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-green-600">{stats.issues?.resolved || 0}</p>
                    <p className="text-xs text-gray-500">Resolved</p>
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-purple-100 rounded-lg">
                    <Users className="h-5 w-5 text-purple-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-gray-900">{stats.leaders?.total || 0}</p>
                    <p className="text-xs text-gray-500">Leaders</p>
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-indigo-100 rounded-lg">
                    <User className="h-5 w-5 text-indigo-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-gray-900">{stats.users?.total || 0}</p>
                    <p className="text-xs text-gray-500">Users</p>
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-teal-100 rounded-lg">
                    <MessageSquare className="h-5 w-5 text-teal-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-gray-900">{stats.responses?.total || 0}</p>
                    <p className="text-xs text-gray-500">Responses</p>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>

        <Tabs defaultValue="issues" className="space-y-6">
          <TabsList className="flex-wrap">
            <TabsTrigger value="issues" data-testid="issues-tab" className="flex items-center gap-1">
              <FileText className="h-4 w-4" /> Issues
            </TabsTrigger>
            <TabsTrigger value="leaders" data-testid="leaders-tab" className="flex items-center gap-1">
              <Users className="h-4 w-4" /> Leaders
            </TabsTrigger>
            <TabsTrigger value="geography" data-testid="geography-tab" className="flex items-center gap-1">
              <MapPin className="h-4 w-4" /> Geography
            </TabsTrigger>
            <TabsTrigger value="responses" data-testid="responses-tab" className="flex items-center gap-1">
              <MessageSquare className="h-4 w-4" /> Responses
            </TabsTrigger>
            <TabsTrigger value="users" data-testid="users-tab" className="flex items-center gap-1">
              <User className="h-4 w-4" /> Users
            </TabsTrigger>
            <TabsTrigger value="ads" data-testid="ads-tab" className="flex items-center gap-1">
              <Megaphone className="h-4 w-4" /> Ads
            </TabsTrigger>
            <TabsTrigger value="rooms" data-testid="rooms-tab" className="flex items-center gap-1" onClick={fetchAdminRooms}>
              <MessageCircle className="h-4 w-4" /> Rooms
            </TabsTrigger>
          </TabsList>

          {/* Leaders Tab */}
          <TabsContent value="leaders" className="space-y-4">
            <div className="flex justify-between items-center">
              <h2 className="text-2xl font-semibold text-gray-900 flex items-center gap-2">
                <Users className="h-6 w-6 text-blue-600" />
                Manage Leaders
              </h2>
              <div className="flex items-center gap-2">
                <Button 
                  onClick={handleDownloadLeaderTemplate}
                  variant="outline"
                  className="border-gray-300 text-gray-700 hover:bg-gray-50"
                  data-testid="download-leader-template-btn"
                >
                  <Download className="mr-2 h-4 w-4" />
                  Template
                </Button>
                <Button 
                  onClick={() => setShowLeaderBulkImportModal(true)}
                  variant="outline"
                  className="border-green-600 text-green-600 hover:bg-green-50"
                  data-testid="bulk-import-leaders-btn"
                >
                  <FileSpreadsheet className="mr-2 h-4 w-4" />
                  Bulk Import
                </Button>
                <Button 
                  onClick={openAddLeaderModal}
                  className="bg-blue-600 hover:bg-blue-700 rounded-full"
                  data-testid="add-leader-btn"
                >
                  <Plus className="mr-2 h-4 w-4" />
                  Add Leader
                </Button>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Photo</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Role</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Party</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Constituency</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Rating</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {leaders.map((leader) => (
                    <tr key={leader.leader_id} data-testid={`leader-row-${leader.leader_id}`}>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <img 
                          src={getImageUrl(leader.photo_url)} 
                          alt={leader.name} 
                          className="w-10 h-10 rounded-full object-cover"
                          onError={(e) => { e.target.src = '/logo.png'; }}
                        />
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">{leader.name}</td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${getRoleBadgeColor(leader.role)}`}>
                          {leader.role}
                        </span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">{leader.party}</td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                        {leader.constituency}, {leader.state}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                          leader.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
                        }`}>
                          {leader.status || 'active'}
                        </span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                        {(leader.avg_rating || 0).toFixed(1)} ({leader.rating_count || 0})
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-right text-sm font-medium">
                        <button
                          type="button"
                          onClick={() => openEditLeaderModal(leader)}
                          className="inline-flex items-center justify-center text-blue-600 hover:text-blue-700 hover:bg-blue-50 mr-2 p-2 rounded transition-colors"
                          data-testid={`edit-leader-${leader.leader_id}`}
                        >
                          <Edit className="h-5 w-5" />
                        </button>
                        <button
                          type="button"
                          onClick={() => confirmDeleteLeader(leader)}
                          disabled={deletingId === leader.leader_id}
                          className="inline-flex items-center justify-center text-red-600 hover:text-red-700 hover:bg-red-50 p-2 rounded transition-colors disabled:opacity-50"
                          data-testid={`delete-leader-${leader.leader_id}`}
                        >
                          {deletingId === leader.leader_id ? (
                            <Loader2 className="h-5 w-5 animate-spin" />
                          ) : (
                            <Trash2 className="h-5 w-5" />
                          )}
                        </button>
                      </td>
                    </tr>
                  ))}
                  {leaders.length === 0 && (
                    <tr>
                      <td colSpan="8" className="px-4 py-8 text-center text-gray-500">
                        No leaders found. Add your first leader or import from CSV.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </TabsContent>

          {/* Issues Tab */}
          {/* Issues Management Tab */}
          {/* Issues Management Tab */}
          <TabsContent value="issues" className="space-y-4">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
              <h2 className="text-2xl font-semibold text-gray-900 flex items-center gap-2">
                <FileText className="h-6 w-6 text-blue-600" />
                Manage Issues
              </h2>
              <div className="text-sm text-gray-500">
                {issuePagination.total_count || adminIssues.length} issues found
                {issuePagination.total_pages > 1 && ` (Page ${issuePagination.page} of ${issuePagination.total_pages})`}
              </div>
            </div>

            {/* Quick Status Tabs */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-2">
              <div className="flex flex-wrap gap-2">
                {[
                  { key: 'all', label: 'All Issues', color: 'bg-gray-100 text-gray-700 hover:bg-gray-200' },
                  { key: 'Pending', label: 'Pending', color: 'bg-yellow-100 text-yellow-700 hover:bg-yellow-200' },
                  { key: 'In Progress', label: 'In Progress', color: 'bg-blue-100 text-blue-700 hover:bg-blue-200' },
                  { key: 'Resolved', label: 'Resolved', color: 'bg-green-100 text-green-700 hover:bg-green-200' }
                ].map(tab => (
                  <button
                    key={tab.key}
                    onClick={() => handleStatusTabChange(tab.key)}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                      activeStatusTab === tab.key 
                        ? tab.color + ' ring-2 ring-offset-1 ring-blue-500' 
                        : 'bg-gray-50 text-gray-600 hover:bg-gray-100'
                    }`}
                    data-testid={`status-tab-${tab.key}`}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Enhanced Filters */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-7 gap-4">
                {/* Search - spans 2 columns */}
                <div className="lg:col-span-2">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input
                      placeholder="Search title, user, location..."
                      value={issueFilters.search}
                      onChange={(e) => setIssueFilters({...issueFilters, search: e.target.value})}
                      className="pl-9"
                      data-testid="admin-issue-search"
                    />
                  </div>
                </div>
                
                {/* State Filter */}
                <div>
                  <Select value={issueFilters.state || 'all'} onValueChange={(v) => setIssueFilters({...issueFilters, state: v === 'all' ? '' : v, district: ''})}>
                    <SelectTrigger>
                      <SelectValue placeholder="State" />
                    </SelectTrigger>
                    <SelectContent className="max-h-[300px] overflow-y-auto">
                      <SelectItem value="all">All States</SelectItem>
                      {Object.keys(states).sort().map(state => (
                        <SelectItem key={state} value={state}>{state}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                {/* Category Filter */}
                <div>
                  <Select value={issueFilters.category || 'all'} onValueChange={(v) => setIssueFilters({...issueFilters, category: v === 'all' ? '' : v})}>
                    <SelectTrigger>
                      <SelectValue placeholder="Category" />
                    </SelectTrigger>
                    <SelectContent className="max-h-[300px] overflow-y-auto">
                      <SelectItem value="all">All Categories</SelectItem>
                      {categories.map(cat => (
                        <SelectItem key={cat} value={cat}>{getCategoryIcon(cat)} {cat}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                {/* Assignment Filter */}
                <div>
                  <Select value={issueFilters.assigned || 'all'} onValueChange={(v) => setIssueFilters({...issueFilters, assigned: v === 'all' ? '' : v})}>
                    <SelectTrigger>
                      <SelectValue placeholder="Assignment" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All</SelectItem>
                      <SelectItem value="assigned">Assigned</SelectItem>
                      <SelectItem value="unassigned">Unassigned</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                {/* Sort Filter */}
                <div>
                  <Select value={issueFilters.sortBy || 'recent'} onValueChange={(v) => setIssueFilters({...issueFilters, sortBy: v})}>
                    <SelectTrigger>
                      <SelectValue placeholder="Sort" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="recent">Most Recent</SelectItem>
                      <SelectItem value="oldest">Oldest First</SelectItem>
                      <SelectItem value="affected">Most Affected</SelectItem>
                      <SelectItem value="comments">Most Comments</SelectItem>
                      <SelectItem value="title">Alphabetical</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                {/* Clear Filters */}
                <div>
                  <Button variant="outline" onClick={() => { clearIssueFilters(); setActiveStatusTab('all'); }} className="w-full">
                    Clear All
                  </Button>
                </div>
              </div>
            </div>

            {issuesLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
              </div>
            ) : (
              <>
                <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Issue</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Category</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Location</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Leader</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Stats</th>
                          <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {adminIssues.map((issue) => (
                        <tr key={issue.issue_id} data-testid={`admin-issue-row-${issue.issue_id}`}>
                          <td className="px-4 py-3">
                            <div className="flex items-start gap-2">
                              {issue.verified && (
                                <span className="flex-shrink-0 px-1.5 py-0.5 bg-yellow-100 text-yellow-700 text-xs font-bold rounded">
                                  VERIFIED
                                </span>
                              )}
                              <div className="min-w-0">
                                <p className="text-sm font-medium text-gray-900 truncate max-w-xs" title={issue.title}>
                                  {issue.title}
                                </p>
                                <p className="text-xs text-gray-500 truncate max-w-xs">
                                  By {issue.user_name || 'Anonymous'} {issue.is_anonymous && '(Anon)'}
                                </p>
                                {issue.leader_response && (
                                  <span className="inline-flex items-center gap-1 mt-1 px-1.5 py-0.5 bg-green-100 text-green-700 text-xs rounded">
                                    <CheckCircle className="h-3 w-3" /> Responded
                                  </span>
                                )}
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            <span className="text-sm">
                              {getCategoryIcon(issue.category)} {issue.category}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <div className="text-sm text-gray-900">{issue.constituency}</div>
                            <div className="text-xs text-gray-500">{issue.state}</div>
                          </td>
                          <td className="px-4 py-3">
                            {issue.leader_name ? (
                              <div className="flex items-center gap-2">
                                {issue.leader_photo && (
                                  <img 
                                    src={getImageUrl(issue.leader_photo)} 
                                    alt="" 
                                    className="w-8 h-8 rounded-full object-cover"
                                    onError={(e) => { e.target.style.display = 'none'; }}
                                  />
                                )}
                                <div className="text-sm text-gray-900">{issue.leader_name}</div>
                              </div>
                            ) : (
                              <span className="text-sm text-gray-400 italic">Unassigned</span>
                            )}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            <Select
                              value={issue.status}
                              onValueChange={(v) => handleUpdateIssueStatus(issue.issue_id, v)}
                              disabled={updatingIssueId === issue.issue_id}
                            >
                              <SelectTrigger className={`w-32 text-xs ${getStatusBadgeColor(issue.status)}`}>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="Pending">Pending</SelectItem>
                                <SelectItem value="In Progress">In Progress</SelectItem>
                                <SelectItem value="Resolved">Resolved</SelectItem>
                              </SelectContent>
                            </Select>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            <div className="text-xs text-gray-600">
                              <div>{issue.vote_count} affected</div>
                              <div>{issue.comment_count || 0} comments</div>
                            </div>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-right">
                            <div className="flex items-center justify-end gap-1">
                              {/* View Details Button */}
                              <button
                                type="button"
                                onClick={() => navigate(`/issues/${issue.issue_id}`)}
                                className="p-2 text-gray-600 hover:text-gray-700 hover:bg-gray-50 rounded transition-colors"
                                title="View Issue Details"
                                data-testid={`view-issue-${issue.issue_id}`}
                              >
                                <Eye className="h-4 w-4" />
                              </button>
                              {/* Verify Button */}
                              <button
                                type="button"
                                onClick={() => handleToggleVerified(issue.issue_id)}
                                disabled={updatingIssueId === issue.issue_id}
                                className={`p-2 rounded transition-colors ${
                                  issue.verified 
                                    ? 'text-yellow-600 bg-yellow-50 hover:bg-yellow-100' 
                                    : 'text-gray-400 hover:text-yellow-600 hover:bg-yellow-50'
                                }`}
                                title={issue.verified ? 'Unverify Issue' : 'Verify Issue (High Priority)'}
                                data-testid={`verify-issue-${issue.issue_id}`}
                              >
                                <Shield className="h-4 w-4" />
                              </button>
                              {/* Assign Leader Button */}
                              <button
                                type="button"
                                onClick={() => openAssignLeaderModal(issue)}
                                className="p-2 text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded transition-colors"
                                title="Assign/Reassign Leader"
                                data-testid={`assign-leader-${issue.issue_id}`}
                              >
                                <UserCheck className="h-4 w-4" />
                              </button>
                              {/* Delete Button */}
                              <button
                                type="button"
                                onClick={() => confirmDeleteIssue(issue)}
                                disabled={deletingId === issue.issue_id}
                                className="p-2 text-red-600 hover:text-red-700 hover:bg-red-50 rounded transition-colors disabled:opacity-50"
                                title="Delete Issue"
                                data-testid={`delete-issue-${issue.issue_id}`}
                              >
                                {deletingId === issue.issue_id ? (
                                  <Loader2 className="h-4 w-4 animate-spin" />
                                ) : (
                                  <Trash2 className="h-4 w-4" />
                                )}
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                      {adminIssues.length === 0 && (
                        <tr>
                          <td colSpan="7" className="px-4 py-8 text-center text-gray-500">
                            No issues found
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Pagination Controls */}
              {issuePagination.total_pages > 1 && (
                <div className="flex items-center justify-between px-4 py-3 bg-white rounded-xl shadow-sm border border-gray-100">
                  <div className="text-sm text-gray-500">
                    Showing {((issuePagination.page - 1) * issuePagination.limit) + 1} to {Math.min(issuePagination.page * issuePagination.limit, issuePagination.total_count)} of {issuePagination.total_count} issues
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handlePageChange(issuePagination.page - 1)}
                      disabled={!issuePagination.has_prev}
                      data-testid="prev-page-btn"
                    >
                      Previous
                    </Button>
                    <div className="flex items-center gap-1">
                      {/* Page numbers */}
                      {Array.from({ length: Math.min(5, issuePagination.total_pages) }, (_, i) => {
                        let pageNum;
                        if (issuePagination.total_pages <= 5) {
                          pageNum = i + 1;
                        } else if (issuePagination.page <= 3) {
                          pageNum = i + 1;
                        } else if (issuePagination.page >= issuePagination.total_pages - 2) {
                          pageNum = issuePagination.total_pages - 4 + i;
                        } else {
                          pageNum = issuePagination.page - 2 + i;
                        }
                        return (
                          <Button
                            key={pageNum}
                            variant={issuePagination.page === pageNum ? "default" : "outline"}
                            size="sm"
                            onClick={() => handlePageChange(pageNum)}
                            className="w-9"
                            data-testid={`page-${pageNum}-btn`}
                          >
                            {pageNum}
                          </Button>
                        );
                      })}
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handlePageChange(issuePagination.page + 1)}
                      disabled={!issuePagination.has_next}
                      data-testid="next-page-btn"
                    >
                      Next
                    </Button>
                  </div>
                </div>
              )}
              </>
            )}
          </TabsContent>
        {/* Responses Moderation Tab */}
          <TabsContent value="responses" className="space-y-4">
            <div className="flex justify-between items-center">
              <h2 className="text-2xl font-semibold text-gray-900 flex items-center gap-2">
                <MessageSquare className="h-6 w-6 text-teal-600" />
                Leader Responses
              </h2>
              <div className="text-sm text-gray-500">
                {adminResponses.length} responses
              </div>
            </div>

            {responsesLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-teal-600" />
              </div>
            ) : (
              <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                {adminResponses.length === 0 ? (
                  <div className="p-8 text-center text-gray-500">
                    No leader responses yet
                  </div>
                ) : (
                  <div className="divide-y divide-gray-200">
                    {adminResponses.map((response) => (
                      <div key={response.response_id} className="p-4 hover:bg-gray-50" data-testid={`response-row-${response.response_id}`}>
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-3 mb-2">
                              {response.leader_photo && (
                                <img 
                                  src={getImageUrl(response.leader_photo)} 
                                  alt={response.leader_name}
                                  className="w-10 h-10 rounded-full object-cover"
                                  onError={(e) => { e.target.style.display = 'none'; }}
                                />
                              )}
                              <div>
                                <p className="font-medium text-gray-900">{response.leader_name}</p>
                                <p className="text-xs text-gray-500">{response.leader_role || 'Leader'}</p>
                              </div>
                            </div>
                            <p className="text-sm text-gray-700 mb-2">{response.response_text}</p>
                            <div className="flex items-center gap-4 text-xs text-gray-500">
                              <span onClick={() => navigate(`/issues/${response.issue_id}`)} className="cursor-pointer hover:text-blue-600">
                                Issue: {response.issue_title || response.issue_id}
                              </span>
                              <span>{response.issue_location}</span>
                              <span>{formatDate(response.timestamp)}</span>
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={() => confirmDeleteResponse(response)}
                            disabled={deletingId === response.response_id}
                            className="p-2 text-red-600 hover:text-red-700 hover:bg-red-50 rounded transition-colors disabled:opacity-50"
                            title="Delete Response"
                          >
                            {deletingId === response.response_id ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <Trash2 className="h-4 w-4" />
                            )}
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </TabsContent>

          {/* Users Management Tab */}
          <TabsContent value="users" className="space-y-4">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
              <h2 className="text-2xl font-semibold text-gray-900 flex items-center gap-2">
                <User className="h-6 w-6 text-indigo-600" />
                User Management
              </h2>
              <div className="flex items-center gap-2">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    placeholder="Search users..."
                    value={userSearch}
                    onChange={(e) => setUserSearch(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && fetchAdminUsers()}
                    className="pl-9 w-64"
                  />
                </div>
                <Button onClick={fetchAdminUsers} variant="outline">Search</Button>
              </div>
            </div>

            {usersLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-indigo-600" />
              </div>
            ) : (
              <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">User</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Issues</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {adminUsers.map((u) => (
                        <tr key={u.user_id} data-testid={`user-row-${u.user_id}`}>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-3">
                              {u.picture ? (
                                <img src={u.picture} alt={u.name} className="w-8 h-8 rounded-full object-cover" />
                              ) : (
                                <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center text-gray-500 text-sm font-medium">
                                  {(u.name || 'U')[0].toUpperCase()}
                                </div>
                              )}
                              <div>
                                <p className="text-sm font-medium text-gray-900">{u.name || 'Unknown'}</p>
                                {u.is_admin && (
                                  <span className="text-xs bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded">Admin</span>
                                )}
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-600">{u.email}</td>
                          <td className="px-4 py-3 text-sm text-gray-600">{u.issue_count || 0}</td>
                          <td className="px-4 py-3">
                            <div className="flex flex-wrap gap-1">
                              {u.is_flagged && (
                                <span className="px-2 py-0.5 text-xs font-medium bg-orange-100 text-orange-700 rounded-full">Flagged</span>
                              )}
                              {u.is_banned && (
                                <span className="px-2 py-0.5 text-xs font-medium bg-red-100 text-red-700 rounded-full">Banned</span>
                              )}
                              {!u.is_flagged && !u.is_banned && (
                                <span className="px-2 py-0.5 text-xs font-medium bg-green-100 text-green-700 rounded-full">Active</span>
                              )}
                            </div>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-right">
                            <div className="flex items-center justify-end gap-1">
                              {/* View User Profile */}
                              <button
                                type="button"
                                onClick={() => handleViewUser(u.user_id)}
                                className="p-2 text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded transition-colors"
                                title="View User Profile"
                              >
                                <Eye className="h-4 w-4" />
                              </button>
                              
                              {u.is_flagged ? (
                                <button
                                  type="button"
                                  onClick={() => handleUnflagUser(u.user_id, u.name)}
                                  className="p-2 text-green-600 hover:text-green-700 hover:bg-green-50 rounded transition-colors"
                                  title="Remove Flag"
                                >
                                  <CheckCircle className="h-4 w-4" />
                                </button>
                              ) : (
                                <button
                                  type="button"
                                  onClick={() => handleFlagUser(u.user_id, u.name)}
                                  className="p-2 text-orange-600 hover:text-orange-700 hover:bg-orange-50 rounded transition-colors"
                                  title="Flag User"
                                  disabled={u.is_admin}
                                >
                                  <Flag className="h-4 w-4" />
                                </button>
                              )}
                              
                              {u.is_banned ? (
                                <button
                                  type="button"
                                  onClick={() => handleUnbanUser(u.user_id, u.name)}
                                  className="p-2 text-green-600 hover:text-green-700 hover:bg-green-50 rounded transition-colors"
                                  title="Unban User"
                                >
                                  <CheckCircle className="h-4 w-4" />
                                </button>
                              ) : (
                                <button
                                  type="button"
                                  onClick={() => handleBanUser(u.user_id, u.name)}
                                  className="p-2 text-red-600 hover:text-red-700 hover:bg-red-50 rounded transition-colors"
                                  title="Ban User"
                                  disabled={u.is_admin}
                                >
                                  <Ban className="h-4 w-4" />
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                      {adminUsers.length === 0 && (
                        <tr>
                          <td colSpan="5" className="px-4 py-8 text-center text-gray-500">
                            No users found
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </TabsContent>

          {/* Geography Tab */}
          <TabsContent value="geography" className="space-y-4">
            <div className="flex justify-between items-center">
              <h2 className="text-2xl font-semibold text-gray-900 flex items-center gap-2">
                <Globe className="h-6 w-6 text-blue-600" />
                Geography Management
              </h2>
            </div>

            {/* Geography Stats */}
            {geoStats && (
              <div className="grid grid-cols-3 gap-4 mb-6">
                <div className="bg-white rounded-lg border p-4">
                  <p className="text-2xl font-bold text-gray-900">{geoStats.states?.total || 0}</p>
                  <p className="text-sm text-gray-500">States/UTs ({geoStats.states?.enabled || 0} enabled)</p>
                </div>
                <div className="bg-white rounded-lg border p-4">
                  <p className="text-2xl font-bold text-gray-900">{geoStats.districts?.total || 0}</p>
                  <p className="text-sm text-gray-500">Districts ({geoStats.districts?.enabled || 0} enabled)</p>
                </div>
                <div className="bg-white rounded-lg border p-4">
                  <p className="text-2xl font-bold text-gray-900">{geoStats.constituencies?.total || 0}</p>
                  <p className="text-sm text-gray-500">Constituencies ({geoStats.constituencies?.enabled || 0} enabled)</p>
                </div>
              </div>
            )}

            {/* State Selection */}
            <div className="bg-white rounded-lg border p-4 mb-4">
              <h3 className="font-semibold text-gray-900 mb-3">Filter by Location</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Select State</Label>
                  <Select value={selectedGeoState} onValueChange={handleGeoStateSelect}>
                    <SelectTrigger className="mt-1">
                      <SelectValue placeholder="Choose a state to view districts" />
                    </SelectTrigger>
                    <SelectContent className="max-h-[300px]">
                      {geoStates.map((state) => (
                        <SelectItem key={state.state_id} value={state.state_id}>
                          {state.name} ({state.district_count || 0} districts)
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {geoDistricts.length > 0 && (
                  <div>
                    <Label>Select District</Label>
                    <Select value={selectedGeoDistrict} onValueChange={handleGeoDistrictSelect}>
                      <SelectTrigger className="mt-1">
                        <SelectValue placeholder="Choose a district to view constituencies" />
                      </SelectTrigger>
                      <SelectContent className="max-h-[300px]">
                        {geoDistricts.map((district) => (
                          <SelectItem key={district.district_id} value={district.district_id}>
                            {district.name} ({district.constituency_count || 0} constituencies)
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>
            </div>

            {/* States Table */}
            {!selectedGeoState && (
              <div className="bg-white rounded-lg border overflow-hidden">
                <div className="px-4 py-3 border-b bg-gray-50">
                  <h3 className="font-semibold text-gray-900">All States/UTs</h3>
                </div>
                <div className="overflow-x-auto max-h-[400px]">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 sticky top-0">
                      <tr>
                        <th className="px-4 py-2 text-left font-medium text-gray-600">Name</th>
                        <th className="px-4 py-2 text-left font-medium text-gray-600">Districts</th>
                        <th className="px-4 py-2 text-left font-medium text-gray-600">Constituencies</th>
                        <th className="px-4 py-2 text-left font-medium text-gray-600">Status</th>
                        <th className="px-4 py-2 text-left font-medium text-gray-600">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {geoStates.map((state) => (
                        <tr key={state.state_id} className="border-t hover:bg-gray-50">
                          <td className="px-4 py-2 font-medium">{state.name}</td>
                          <td className="px-4 py-2">{state.district_count || 0}</td>
                          <td className="px-4 py-2">{state.constituency_count || 0}</td>
                          <td className="px-4 py-2">
                            <span className={`px-2 py-1 rounded-full text-xs ${
                              state.is_enabled !== false ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                            }`}>
                              {state.is_enabled !== false ? 'Enabled' : 'Disabled'}
                            </span>
                          </td>
                          <td className="px-4 py-2">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => openEditGeoModal(state, 'state')}
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Districts Table */}
            {selectedGeoState && !selectedGeoDistrict && (
              <div className="bg-white rounded-lg border overflow-hidden">
                <div className="px-4 py-3 border-b bg-gray-50 flex justify-between items-center">
                  <h3 className="font-semibold text-gray-900">
                    Districts in {geoStates.find(s => s.state_id === selectedGeoState)?.name}
                  </h3>
                  <div className="flex items-center gap-2">
                    <Button 
                      size="sm" 
                      onClick={() => setShowAddDistrictModal(true)}
                      className="bg-green-600 hover:bg-green-700"
                    >
                      <Plus className="h-4 w-4 mr-1" /> Add District
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => setSelectedGeoState('')}>
                      Back to States
                    </Button>
                  </div>
                </div>
                <div className="overflow-x-auto max-h-[400px]">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 sticky top-0">
                      <tr>
                        <th className="px-4 py-2 text-left font-medium text-gray-600">Name</th>
                        <th className="px-4 py-2 text-left font-medium text-gray-600">Code</th>
                        <th className="px-4 py-2 text-left font-medium text-gray-600">Constituencies</th>
                        <th className="px-4 py-2 text-left font-medium text-gray-600">Status</th>
                        <th className="px-4 py-2 text-left font-medium text-gray-600">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {geoDistricts.length === 0 ? (
                        <tr>
                          <td colSpan="5" className="px-4 py-8 text-center text-gray-500">
                            No districts found. Click "Add District" to create one.
                          </td>
                        </tr>
                      ) : (
                        geoDistricts.map((district) => (
                          <tr key={district.district_id} className="border-t hover:bg-gray-50">
                            <td className="px-4 py-2 font-medium">{district.name}</td>
                            <td className="px-4 py-2 text-gray-500">{district.code || '-'}</td>
                            <td className="px-4 py-2">{district.constituency_count || 0}</td>
                            <td className="px-4 py-2">
                              <span className={`px-2 py-1 rounded-full text-xs ${
                                district.is_enabled !== false ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                              }`}>
                                {district.is_enabled !== false ? 'Enabled' : 'Disabled'}
                              </span>
                            </td>
                            <td className="px-4 py-2">
                              <div className="flex items-center gap-1">
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => openEditGeoModal(district, 'district')}
                                  title="Edit"
                                >
                                  <Edit className="h-4 w-4" />
                                </Button>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => openDeleteGeoModal(district, 'district')}
                                  className="text-red-600 hover:text-red-800 hover:bg-red-50"
                                  title="Delete"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Constituencies Table */}
            {selectedGeoDistrict && (
              <div className="bg-white rounded-lg border overflow-hidden">
                <div className="px-4 py-3 border-b bg-gray-50 flex justify-between items-center">
                  <h3 className="font-semibold text-gray-900">
                    Constituencies in {geoDistricts.find(d => d.district_id === selectedGeoDistrict)?.name}
                  </h3>
                  <div className="flex items-center gap-2">
                    <Button 
                      size="sm" 
                      onClick={() => setShowAddConstituencyModal(true)}
                      className="bg-green-600 hover:bg-green-700"
                    >
                      <Plus className="h-4 w-4 mr-1" /> Add Constituency
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => setSelectedGeoDistrict('')}>
                      Back to Districts
                    </Button>
                  </div>
                </div>
                <div className="overflow-x-auto max-h-[400px]">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 sticky top-0">
                      <tr>
                        <th className="px-4 py-2 text-left font-medium text-gray-600">#</th>
                        <th className="px-4 py-2 text-left font-medium text-gray-600">Name</th>
                        <th className="px-4 py-2 text-left font-medium text-gray-600">Reserved</th>
                        <th className="px-4 py-2 text-left font-medium text-gray-600">Type</th>
                        <th className="px-4 py-2 text-left font-medium text-gray-600">Status</th>
                        <th className="px-4 py-2 text-left font-medium text-gray-600">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {geoConstituencies.length === 0 ? (
                        <tr>
                          <td colSpan="6" className="px-4 py-8 text-center text-gray-500">
                            No constituencies found. Click "Add Constituency" to create one.
                          </td>
                        </tr>
                      ) : (
                        geoConstituencies.map((const_, idx) => (
                          <tr key={const_.constituency_id} className="border-t hover:bg-gray-50">
                            <td className="px-4 py-2 text-gray-500">{const_.number || idx + 1}</td>
                            <td className="px-4 py-2 font-medium">{const_.name}</td>
                            <td className="px-4 py-2">
                              {const_.reserved_category ? (
                                <span className="px-2 py-1 rounded-full text-xs bg-orange-100 text-orange-800">
                                  {const_.reserved_category}
                                </span>
                              ) : '-'}
                            </td>
                            <td className="px-4 py-2 capitalize">{const_.type || 'assembly'}</td>
                            <td className="px-4 py-2">
                              <span className={`px-2 py-1 rounded-full text-xs ${
                                const_.is_enabled !== false ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                              }`}>
                                {const_.is_enabled !== false ? 'Enabled' : 'Disabled'}
                              </span>
                            </td>
                            <td className="px-4 py-2">
                              <div className="flex items-center gap-1">
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => openEditGeoModal(const_, 'constituency')}
                                  title="Edit"
                                >
                                  <Edit className="h-4 w-4" />
                                </Button>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => openDeleteGeoModal(const_, 'constituency')}
                                  className="text-red-600 hover:text-red-800 hover:bg-red-50"
                                  title="Delete"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Empty states - only show when district selected but no data exists */}
            {selectedGeoState && !selectedGeoDistrict && geoDistricts.length === 0 && (
              <div className="bg-white rounded-lg border p-8 text-center text-gray-500">
                <MapPin className="h-12 w-12 mx-auto text-gray-300 mb-3" />
                <p className="mb-4">No districts found for this state.</p>
                <Button 
                  onClick={() => setShowAddDistrictModal(true)}
                  className="bg-green-600 hover:bg-green-700"
                >
                  <Plus className="h-4 w-4 mr-1" /> Add First District
                </Button>
              </div>
            )}
          </TabsContent>

          {/* Ads Tab */}
          <TabsContent value="ads" className="space-y-4">
            <div className="flex justify-between items-center">
              <h3 className="text-lg font-semibold">Ads Management</h3>
              <Button onClick={() => setShowAddAdModal(true)} className="bg-purple-600 hover:bg-purple-700">
                <Plus className="h-4 w-4 mr-2" /> Create Ad
              </Button>
            </div>

            {adsLoading ? (
              <div className="flex justify-center py-8">
                <Loader2 className="h-8 w-8 animate-spin text-purple-600" />
              </div>
            ) : ads.length === 0 ? (
              <div className="text-center py-12 bg-gray-50 rounded-lg">
                <Megaphone className="h-12 w-12 mx-auto text-gray-300 mb-3" />
                <p className="text-gray-500">No ads created yet</p>
                <Button 
                  onClick={() => setShowAddAdModal(true)} 
                  className="mt-4 bg-purple-600 hover:bg-purple-700"
                >
                  <Plus className="h-4 w-4 mr-2" /> Create Your First Ad
                </Button>
              </div>
            ) : (
              <div className="bg-white rounded-lg border overflow-hidden">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Image</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Title</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Link</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Created</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {ads.map((ad) => (
                      <tr key={ad.ad_id} className="hover:bg-gray-50">
                        <td className="px-4 py-3">
                          {ad.image_url ? (
                            <img
                              src={ad.image_url.startsWith('http') ? ad.image_url : `${API_URL}${ad.image_url}`}
                              alt={ad.title}
                              className="w-16 h-12 object-cover rounded"
                            />
                          ) : (
                            <div className="w-16 h-12 bg-gray-100 rounded flex items-center justify-center">
                              <Image className="h-5 w-5 text-gray-400" />
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <div>
                            <p className="font-medium text-gray-900">{ad.title}</p>
                            {ad.description && (
                              <p className="text-xs text-gray-500 truncate max-w-xs">{ad.description}</p>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          {ad.link ? (
                            <a
                              href={ad.link}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-blue-600 hover:underline text-sm flex items-center gap-1"
                            >
                              <LinkIcon className="h-3 w-3" />
                              Link
                            </a>
                          ) : (
                            <span className="text-gray-400 text-sm">No link</span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                            ad.is_active 
                              ? 'bg-green-100 text-green-700' 
                              : 'bg-gray-100 text-gray-600'
                          }`}>
                            {ad.is_active ? (
                              <>
                                <CheckCircle className="h-3 w-3" />
                                Active
                              </>
                            ) : (
                              <>
                                <XCircle className="h-3 w-3" />
                                Inactive
                              </>
                            )}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-500">
                          {formatDate(ad.created_at)}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <Button
                              onClick={() => openEditAdModal(ad)}
                              variant="outline"
                              size="sm"
                              className="text-blue-600 border-blue-200 hover:bg-blue-50"
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button
                              onClick={() => handleToggleAd(ad.ad_id)}
                              disabled={togglingAdId === ad.ad_id}
                              variant="outline"
                              size="sm"
                              className={ad.is_active ? 'text-orange-600 border-orange-200' : 'text-green-600 border-green-200'}
                            >
                              {togglingAdId === ad.ad_id ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : ad.is_active ? (
                                <ToggleRight className="h-4 w-4" />
                              ) : (
                                <ToggleLeft className="h-4 w-4" />
                              )}
                            </Button>
                            <Button
                              onClick={() => handleDeleteAd(ad.ad_id)}
                              disabled={deletingAdId === ad.ad_id}
                              variant="outline"
                              size="sm"
                              className="text-red-600 border-red-200 hover:bg-red-50"
                            >
                              {deletingAdId === ad.ad_id ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : (
                                <Trash2 className="h-4 w-4" />
                              )}
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </TabsContent>

          {/* Rooms Tab */}
          <TabsContent value="rooms" className="space-y-4">
            <div className="flex justify-between items-center">
              <h2 className="text-2xl font-semibold text-gray-900 flex items-center gap-2">
                <MessageCircle className="h-6 w-6 text-purple-600" />
                Manage Rooms
              </h2>
              <div className="flex items-center gap-2">
                <Select value={roomStatusFilter} onValueChange={(val) => { setRoomStatusFilter(val); }}>
                  <SelectTrigger className="w-40">
                    <SelectValue placeholder="Filter by status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Rooms</SelectItem>
                    <SelectItem value="waiting">Waiting</SelectItem>
                    <SelectItem value="ready">Ready</SelectItem>
                    <SelectItem value="live">Live</SelectItem>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="ended">Ended</SelectItem>
                  </SelectContent>
                </Select>
                <Button onClick={fetchAdminRooms} variant="outline" data-testid="refresh-rooms-btn">
                  Refresh
                </Button>
              </div>
            </div>

            {roomsLoading ? (
              <div className="flex items-center justify-center h-64">
                <Loader2 className="h-8 w-8 animate-spin text-purple-600" />
              </div>
            ) : adminRooms.length === 0 ? (
              <div className="text-center py-12 bg-white rounded-lg border">
                <MessageCircle className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500">No rooms found</p>
                <p className="text-sm text-gray-400 mt-1">Click Refresh to load rooms</p>
              </div>
            ) : (
              <div className="overflow-x-auto bg-white rounded-lg border">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Topic</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Created By</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Created At</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {adminRooms.map((room) => (
                      <tr key={room.room_id} className="hover:bg-gray-50">
                        <td className="px-4 py-3">
                          <div className="max-w-xs">
                            <p className="text-sm font-medium text-gray-900 truncate">{room.topic || 'Untitled Room'}</p>
                            <p className="text-xs text-gray-500">{room.room_id}</p>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <p className="text-sm text-gray-900">{room.creator_name || 'Unknown'}</p>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            room.status === 'live' ? 'bg-green-100 text-green-800' :
                            room.status === 'ready' ? 'bg-blue-100 text-blue-800' :
                            room.status === 'waiting' ? 'bg-yellow-100 text-yellow-800' :
                            room.status === 'active' ? 'bg-green-100 text-green-800' :
                            room.status === 'ended' ? 'bg-gray-100 text-gray-800' :
                            'bg-gray-100 text-gray-800'
                          }`}>
                            {room.status || 'Unknown'}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <p className="text-sm text-gray-500">{formatDate(room.created_at)}</p>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => navigate(`/rooms/${room.room_id}`)}
                              title="View Room"
                              data-testid={`view-room-${room.room_id}`}
                            >
                              <Eye className="h-4 w-4 text-blue-600" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setDeleteConfirm({ type: 'room', id: room.room_id, name: room.topic || 'this room' })}
                              disabled={deletingRoomId === room.room_id}
                              title="Delete Room"
                              data-testid={`delete-room-${room.room_id}`}
                            >
                              {deletingRoomId === room.room_id ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : (
                                <Trash2 className="h-4 w-4 text-red-600" />
                              )}
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </TabsContent>
        </Tabs>

        {/* Assign Leader Modal */}
        <Dialog open={showAssignLeaderModal} onOpenChange={setShowAssignLeaderModal}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <UserCheck className="h-5 w-5 text-blue-600" />
                Assign Leader
              </DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              {selectedIssueForAssign && (
                <div className="bg-gray-50 p-3 rounded-lg">
                  <p className="text-sm font-medium text-gray-900">{selectedIssueForAssign.title}</p>
                  <p className="text-xs text-gray-500">{selectedIssueForAssign.constituency}, {selectedIssueForAssign.state}</p>
                </div>
              )}
              
              <div>
                <Label>Select Leader</Label>
                <Select value={assignLeaderId} onValueChange={setAssignLeaderId}>
                  <SelectTrigger className="mt-2">
                    <SelectValue placeholder="Choose a leader..." />
                  </SelectTrigger>
                  <SelectContent>
                    {leaders.map((leader) => (
                      <SelectItem key={leader.leader_id} value={leader.leader_id}>
                        {leader.name} ({leader.role}) - {leader.constituency}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowAssignLeaderModal(false)}>
                Cancel
              </Button>
              <Button 
                type="button" 
                className="bg-blue-600 hover:bg-blue-700"
                onClick={handleAssignLeader}
                disabled={!assignLeaderId || updatingIssueId}
              >
                {updatingIssueId ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    Assigning...
                  </>
                ) : (
                  'Assign Leader'
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>


        {/* Add Leader Modal */}
        <Dialog open={showAddLeaderModal} onOpenChange={setShowAddLeaderModal}>
          <DialogContent className="sm:max-w-lg max-h-[90vh] flex flex-col">
            <DialogHeader className="flex-shrink-0">
              <DialogTitle>Add New Leader</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreateLeader} className="flex flex-col flex-1 overflow-hidden">
              <div className="flex-1 overflow-y-auto px-1 space-y-4">
                <div>
                  <Label htmlFor="leader-photo">Photo</Label>
                  <label
                    htmlFor="leader-photo"
                    className="mt-2 flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50"
                  >
                    {leaderImagePreview ? (
                      <img src={leaderImagePreview} alt="Preview" className="h-full object-contain" />
                    ) : (
                      <div className="flex flex-col items-center">
                        <Upload className="h-8 w-8 text-gray-400 mb-1" />
                        <p className="text-sm text-gray-500">Click to upload</p>
                      </div>
                    )}
                    <input
                      id="leader-photo"
                      type="file"
                      accept="image/*"
                      onChange={handleLeaderImageChange}
                      className="hidden"
                    />
                  </label>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="leader-name">Name *</Label>
                    <Input
                      id="leader-name"
                      value={leaderForm.name}
                      onChange={(e) => setLeaderForm({...leaderForm, name: e.target.value})}
                      placeholder="Leader name"
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="leader-role">Role *</Label>
                    <Select value={leaderForm.role} onValueChange={(v) => setLeaderForm({...leaderForm, role: v})}>
                      <SelectTrigger id="leader-role">
                        <SelectValue placeholder="Select role" />
                      </SelectTrigger>
                      <SelectContent>
                        {roles.map(role => (
                          <SelectItem key={role} value={role}>{role}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="leader-party">Party *</Label>
                    <Input
                      id="leader-party"
                      value={leaderForm.party}
                      onChange={(e) => setLeaderForm({...leaderForm, party: e.target.value})}
                      placeholder="Party name"
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="leader-state">State *</Label>
                    {leaderGeoLoading.states ? (
                      <div className="flex items-center h-10 px-3 border rounded-md bg-gray-50">
                        <Loader2 className="h-4 w-4 animate-spin mr-2" />
                        <span className="text-sm text-gray-500">Loading states...</span>
                      </div>
                    ) : leaderGeoStates.length > 0 ? (
                      <Select 
                        value={leaderSelectedStateId} 
                        onValueChange={(stateId) => {
                          const state = leaderGeoStates.find(s => s.state_id === stateId);
                          if (state) handleLeaderStateSelect(stateId, state.name);
                        }}
                      >
                        <SelectTrigger id="leader-state">
                          <SelectValue placeholder="Select state" />
                        </SelectTrigger>
                        <SelectContent>
                          {leaderGeoStates.map((state) => (
                            <SelectItem key={state.state_id} value={state.state_id}>
                              {state.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <Input
                        id="leader-state"
                        value={leaderForm.state}
                        onChange={(e) => setLeaderForm({...leaderForm, state: e.target.value})}
                        placeholder="State (manual entry)"
                        required
                      />
                    )}
                    {leaderForm.state && leaderSelectedStateId && (
                      <p className="text-xs text-gray-500 mt-1">Selected: {leaderForm.state}</p>
                    )}
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="leader-district">District</Label>
                    {leaderGeoLoading.districts ? (
                      <div className="flex items-center h-10 px-3 border rounded-md bg-gray-50">
                        <Loader2 className="h-4 w-4 animate-spin mr-2" />
                        <span className="text-sm text-gray-500">Loading...</span>
                      </div>
                    ) : leaderGeoDistricts.length > 0 ? (
                      <Select 
                        value={leaderSelectedDistrictId || '_none'} 
                        onValueChange={(districtId) => {
                          if (districtId === '_none') {
                            setLeaderSelectedDistrictId('');
                            setLeaderForm(prev => ({ ...prev, district: '' }));
                            fetchLeaderGeoConstituencies(leaderSelectedStateId);
                          } else {
                            const district = leaderGeoDistricts.find(d => d.district_id === districtId);
                            if (district) handleLeaderDistrictSelect(districtId, district.name);
                          }
                        }}
                      >
                        <SelectTrigger id="leader-district">
                          <SelectValue placeholder="Select district (optional)" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="_none">-- No specific district --</SelectItem>
                          {leaderGeoDistricts.map((district) => (
                            <SelectItem key={district.district_id} value={district.district_id}>
                              {district.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <Input
                        id="leader-district"
                        value={leaderForm.district}
                        onChange={(e) => setLeaderForm({...leaderForm, district: e.target.value})}
                        placeholder="District (optional)"
                      />
                    )}
                  </div>
                  <div>
                    <Label htmlFor="leader-constituency">Constituency *</Label>
                    {leaderGeoLoading.constituencies ? (
                      <div className="flex items-center h-10 px-3 border rounded-md bg-gray-50">
                        <Loader2 className="h-4 w-4 animate-spin mr-2" />
                        <span className="text-sm text-gray-500">Loading...</span>
                      </div>
                    ) : leaderGeoConstituencies.length > 0 ? (
                      <Select 
                        value={leaderForm.constituency || '_manual'} 
                        onValueChange={(val) => {
                          if (val !== '_manual') {
                            handleLeaderConstituencySelect(val);
                          }
                        }}
                      >
                        <SelectTrigger id="leader-constituency">
                          <SelectValue placeholder="Select constituency" />
                        </SelectTrigger>
                        <SelectContent>
                          {leaderGeoConstituencies.map((constituency) => (
                            <SelectItem key={constituency.constituency_id || constituency.name} value={constituency.name}>
                              {constituency.name}
                            </SelectItem>
                          ))}
                          <SelectItem value="_manual">-- Enter manually --</SelectItem>
                        </SelectContent>
                      </Select>
                    ) : leaderSelectedStateId ? (
                      <Input
                        id="leader-constituency"
                        value={leaderForm.constituency}
                        onChange={(e) => setLeaderForm({...leaderForm, constituency: e.target.value})}
                        placeholder="Constituency (manual entry)"
                        required
                      />
                    ) : (
                      <Input
                        id="leader-constituency"
                        value={leaderForm.constituency}
                        onChange={(e) => setLeaderForm({...leaderForm, constituency: e.target.value})}
                        placeholder="Select state first or enter manually"
                        required
                      />
                    )}
                    {leaderForm.constituency === '_manual' && (
                      <Input
                        className="mt-2"
                        value=""
                        onChange={(e) => setLeaderForm({...leaderForm, constituency: e.target.value})}
                        placeholder="Enter constituency name"
                        required
                      />
                    )}
                  </div>
                </div>
                <div>
                  <Label htmlFor="leader-status">Status</Label>
                  <Select value={leaderForm.status} onValueChange={(v) => setLeaderForm({...leaderForm, status: v})}>
                    <SelectTrigger id="leader-status">
                      <SelectValue placeholder="Select status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="former">Former</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="leader-bio">Biography</Label>
                  <Textarea
                    id="leader-bio"
                    value={leaderForm.bio}
                    onChange={(e) => setLeaderForm({...leaderForm, bio: e.target.value})}
                    rows={4}
                    placeholder="Full biography (use Enter key for paragraphs)..."
                    className="font-normal"
                  />
                  <p className="text-xs text-gray-500 mt-1">Press Enter twice for new paragraph</p>
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label htmlFor="leader-birth-date">Birth Date</Label>
                    <Input
                      id="leader-birth-date"
                      value={leaderForm.birth_date}
                      onChange={(e) => setLeaderForm({...leaderForm, birth_date: e.target.value})}
                      placeholder="e.g., 1 February 1969"
                    />
                  </div>
                  <div>
                    <Label htmlFor="leader-position">Current Position</Label>
                    <Input
                      id="leader-position"
                      value={leaderForm.position}
                      onChange={(e) => setLeaderForm({...leaderForm, position: e.target.value})}
                      placeholder="e.g., Chief Minister"
                    />
                  </div>
                  <div>
                    <Label htmlFor="leader-assumed-office">Assumed Office</Label>
                    <Input
                      id="leader-assumed-office"
                      value={leaderForm.assumed_office}
                      onChange={(e) => setLeaderForm({...leaderForm, assumed_office: e.target.value})}
                      placeholder="e.g., 10 May 2021"
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="leader-early-life">Early Life & Education</Label>
                  <Textarea
                    id="leader-early-life"
                    value={leaderForm.early_life_education}
                    onChange={(e) => setLeaderForm({...leaderForm, early_life_education: e.target.value})}
                    rows={3}
                    placeholder="Birth, education, family background (use Enter for new lines)..."
                    className="font-normal"
                  />
                </div>
                <div>
                  <Label htmlFor="leader-achievements">Achievements</Label>
                  <Textarea
                    id="leader-achievements"
                    value={leaderForm.achievements}
                    onChange={(e) => setLeaderForm({...leaderForm, achievements: e.target.value})}
                    rows={3}
                    placeholder="List achievements (use Enter for new lines)..."
                    className="font-normal"
                  />
                </div>
                <div>
                  <Label htmlFor="leader-controversies">Controversies</Label>
                  <Textarea
                    id="leader-controversies"
                    value={leaderForm.controversies}
                    onChange={(e) => setLeaderForm({...leaderForm, controversies: e.target.value})}
                    rows={3}
                    placeholder="List controversies if any (use Enter for new lines)..."
                    className="font-normal"
                  />
                </div>
              </div>
              <DialogFooter className="flex-shrink-0 pt-4 border-t mt-4">
                <Button type="button" variant="outline" onClick={closeAddLeaderModal}>
                  Cancel
                </Button>
                <Button type="submit" className="bg-blue-600 hover:bg-blue-700" disabled={isSubmitting}>
                  {isSubmitting ? 'Adding...' : 'Add Leader'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Edit Leader Modal */}
        <Dialog open={showEditLeaderModal} onOpenChange={setShowEditLeaderModal}>
          <DialogContent className="sm:max-w-lg max-h-[90vh] flex flex-col">
            <DialogHeader className="flex-shrink-0">
              <DialogTitle>Edit Leader</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleUpdateLeader} className="flex flex-col flex-1 overflow-hidden">
              <div className="flex-1 overflow-y-auto px-1 space-y-4">
                <div>
                  <Label htmlFor="edit-leader-photo">Photo</Label>
                  <label
                    htmlFor="edit-leader-photo"
                    className="mt-2 flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50"
                  >
                    {leaderImagePreview ? (
                      <img src={leaderImagePreview} alt="Preview" className="h-full object-contain" />
                    ) : (
                      <div className="flex flex-col items-center">
                        <Upload className="h-8 w-8 text-gray-400 mb-1" />
                        <p className="text-sm text-gray-500">Click to upload new image</p>
                      </div>
                    )}
                    <input
                      id="edit-leader-photo"
                      type="file"
                      accept="image/*"
                      onChange={handleLeaderImageChange}
                      className="hidden"
                    />
                  </label>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="edit-leader-name">Name *</Label>
                    <Input
                      id="edit-leader-name"
                      value={leaderForm.name}
                      onChange={(e) => setLeaderForm({...leaderForm, name: e.target.value})}
                      placeholder="Leader name"
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="edit-leader-role">Role *</Label>
                    <Select value={leaderForm.role} onValueChange={(v) => setLeaderForm({...leaderForm, role: v})}>
                      <SelectTrigger id="edit-leader-role">
                        <SelectValue placeholder="Select role" />
                      </SelectTrigger>
                      <SelectContent>
                        {roles.map(role => (
                          <SelectItem key={role} value={role}>{role}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="edit-leader-party">Party *</Label>
                    <Input
                      id="edit-leader-party"
                      value={leaderForm.party}
                      onChange={(e) => setLeaderForm({...leaderForm, party: e.target.value})}
                      placeholder="Party name"
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="edit-leader-state">State *</Label>
                    <Input
                      id="edit-leader-state"
                      value={leaderForm.state}
                      onChange={(e) => setLeaderForm({...leaderForm, state: e.target.value})}
                      placeholder="State"
                      required
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="edit-leader-district">District</Label>
                    <Input
                      id="edit-leader-district"
                      value={leaderForm.district}
                      onChange={(e) => setLeaderForm({...leaderForm, district: e.target.value})}
                      placeholder="District (optional)"
                    />
                  </div>
                  <div>
                    <Label htmlFor="edit-leader-constituency">Constituency *</Label>
                    <Input
                      id="edit-leader-constituency"
                      value={leaderForm.constituency}
                      onChange={(e) => setLeaderForm({...leaderForm, constituency: e.target.value})}
                      placeholder="Constituency"
                      required
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="edit-leader-status">Status</Label>
                  <Select value={leaderForm.status} onValueChange={(v) => setLeaderForm({...leaderForm, status: v})}>
                    <SelectTrigger id="edit-leader-status">
                      <SelectValue placeholder="Select status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="former">Former</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="edit-leader-bio">Biography</Label>
                  <Textarea
                    id="edit-leader-bio"
                    value={leaderForm.bio}
                    onChange={(e) => setLeaderForm({...leaderForm, bio: e.target.value})}
                    rows={4}
                    placeholder="Full biography (use Enter key for paragraphs)..."
                    className="font-normal"
                  />
                  <p className="text-xs text-gray-500 mt-1">Press Enter twice for new paragraph</p>
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label htmlFor="edit-leader-birth-date">Birth Date</Label>
                    <Input
                      id="edit-leader-birth-date"
                      value={leaderForm.birth_date}
                      onChange={(e) => setLeaderForm({...leaderForm, birth_date: e.target.value})}
                      placeholder="e.g., 1 February 1969"
                    />
                  </div>
                  <div>
                    <Label htmlFor="edit-leader-position">Current Position</Label>
                    <Input
                      id="edit-leader-position"
                      value={leaderForm.position}
                      onChange={(e) => setLeaderForm({...leaderForm, position: e.target.value})}
                      placeholder="e.g., Chief Minister"
                    />
                  </div>
                  <div>
                    <Label htmlFor="edit-leader-assumed-office">Assumed Office</Label>
                    <Input
                      id="edit-leader-assumed-office"
                      value={leaderForm.assumed_office}
                      onChange={(e) => setLeaderForm({...leaderForm, assumed_office: e.target.value})}
                      placeholder="e.g., 10 May 2021"
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="edit-leader-early-life">Early Life & Education</Label>
                  <Textarea
                    id="edit-leader-early-life"
                    value={leaderForm.early_life_education}
                    onChange={(e) => setLeaderForm({...leaderForm, early_life_education: e.target.value})}
                    rows={3}
                    placeholder="Birth, education, family background (use Enter for new lines)..."
                    className="font-normal"
                  />
                </div>
                <div>
                  <Label htmlFor="edit-leader-achievements">Achievements</Label>
                  <Textarea
                    id="edit-leader-achievements"
                    value={leaderForm.achievements}
                    onChange={(e) => setLeaderForm({...leaderForm, achievements: e.target.value})}
                    rows={3}
                    placeholder="List achievements (use Enter for new lines)..."
                    className="font-normal"
                  />
                </div>
                <div>
                  <Label htmlFor="edit-leader-controversies">Controversies</Label>
                  <Textarea
                    id="edit-leader-controversies"
                    value={leaderForm.controversies}
                    onChange={(e) => setLeaderForm({...leaderForm, controversies: e.target.value})}
                    rows={3}
                    placeholder="List controversies if any (use Enter for new lines)..."
                    className="font-normal"
                  />
                </div>
              </div>
              <DialogFooter className="flex-shrink-0 pt-4 border-t mt-4">
                <Button type="button" variant="outline" onClick={closeEditLeaderModal}>
                  Cancel
                </Button>
                <Button type="submit" className="bg-blue-600 hover:bg-blue-700" disabled={isSubmitting}>
                  {isSubmitting ? 'Updating...' : 'Update Leader'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Leader Bulk Import Modal */}
        <Dialog open={showLeaderBulkImportModal} onOpenChange={setShowLeaderBulkImportModal}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <FileSpreadsheet className="h-5 w-5 text-green-600" />
                Bulk Import Leaders
              </DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-sm text-blue-800 font-medium mb-2">Instructions:</p>
                <ol className="text-sm text-blue-700 list-decimal list-inside space-y-1">
                  <li>Download the CSV template</li>
                  <li>Fill in leader data (name, role, party, state, district, constituency)</li>
                  <li>Save as CSV or Excel file</li>
                  <li>Upload the file below</li>
                </ol>
                <p className="text-xs text-blue-600 mt-2">Note: New leaders with same role & constituency will mark previous ones as "former"</p>
              </div>

              <Button
                type="button"
                variant="outline"
                onClick={handleDownloadLeaderTemplate}
                className="w-full border-gray-300"
              >
                <Download className="mr-2 h-4 w-4" />
                Download CSV Template
              </Button>

              <div>
                <Label htmlFor="leader-bulk-file">Select CSV or Excel File</Label>
                <div className="mt-2">
                  <label
                    htmlFor="leader-bulk-file"
                    className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors"
                  >
                    {leaderBulkFile ? (
                      <div className="flex flex-col items-center">
                        <FileSpreadsheet className="h-10 w-10 text-green-500 mb-2" />
                        <p className="text-sm font-medium text-gray-700">{leaderBulkFile.name}</p>
                        <p className="text-xs text-gray-500 mt-1">Click to change file</p>
                      </div>
                    ) : (
                      <div className="flex flex-col items-center">
                        <Upload className="h-10 w-10 text-gray-400 mb-2" />
                        <p className="text-sm text-gray-500">Click to upload CSV or Excel</p>
                        <p className="text-xs text-gray-400 mt-1">.csv, .xlsx, .xls</p>
                      </div>
                    )}
                    <input
                      id="leader-bulk-file"
                      type="file"
                      accept=".csv,.xlsx,.xls"
                      onChange={handleLeaderBulkFileChange}
                      className="hidden"
                    />
                  </label>
                </div>
              </div>

              {leaderBulkImportResult && (
                <div className={`rounded-lg p-4 ${leaderBulkImportResult.error ? 'bg-red-50 border border-red-200' : 'bg-gray-50 border border-gray-200'}`}>
                  {leaderBulkImportResult.error ? (
                    <div className="flex items-center gap-2 text-red-700">
                      <XCircle className="h-5 w-5" />
                      <span className="font-medium">{leaderBulkImportResult.error}</span>
                    </div>
                  ) : (
                    <>
                      <div className="flex items-center justify-between mb-3">
                        <span className="font-medium text-gray-900">Import Results</span>
                        <span className="text-sm text-gray-500">Total: {leaderBulkImportResult.total}</span>
                      </div>
                      
                      <div className="grid grid-cols-3 gap-2 mb-3">
                        <div className="flex items-center gap-2 bg-green-100 rounded-lg px-3 py-2">
                          <CheckCircle className="h-4 w-4 text-green-600" />
                          <span className="text-sm font-medium text-green-700">{leaderBulkImportResult.success} Added</span>
                        </div>
                        <div className="flex items-center gap-2 bg-yellow-100 rounded-lg px-3 py-2">
                          <span className="text-sm font-medium text-yellow-700">{leaderBulkImportResult.marked_former || 0} Former</span>
                        </div>
                        <div className="flex items-center gap-2 bg-red-100 rounded-lg px-3 py-2">
                          <XCircle className="h-4 w-4 text-red-600" />
                          <span className="text-sm font-medium text-red-700">{leaderBulkImportResult.failed} Failed</span>
                        </div>
                      </div>

                      {leaderBulkImportResult.errors && leaderBulkImportResult.errors.length > 0 && (
                        <div className="mt-3">
                          <p className="text-sm font-medium text-gray-700 mb-2">Errors:</p>
                          <div className="max-h-32 overflow-y-auto space-y-1">
                            {leaderBulkImportResult.errors.map((err, idx) => (
                              <p key={idx} className="text-xs text-red-600 bg-red-50 px-2 py-1 rounded">
                                Row {err.row}: {err.error}
                              </p>
                            ))}
                          </div>
                        </div>
                      )}

                      {leaderBulkImportResult.created && leaderBulkImportResult.created.length > 0 && (
                        <div className="mt-3">
                          <p className="text-sm font-medium text-gray-700 mb-2">Created:</p>
                          <div className="max-h-32 overflow-y-auto space-y-1">
                            {leaderBulkImportResult.created.slice(0, 5).map((leader, idx) => (
                              <p key={idx} className="text-xs text-green-600 bg-green-50 px-2 py-1 rounded">
                                {leader.name} - {leader.role} ({leader.constituency})
                              </p>
                            ))}
                            {leaderBulkImportResult.created.length > 5 && (
                              <p className="text-xs text-gray-500 px-2">
                                ...and {leaderBulkImportResult.created.length - 5} more
                              </p>
                            )}
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={closeLeaderBulkImportModal}>
                {leaderBulkImportResult ? 'Close' : 'Cancel'}
              </Button>
              {!leaderBulkImportResult && (
                <Button
                  type="button"
                  className="bg-green-600 hover:bg-green-700 text-white"
                  onClick={handleLeaderBulkImport}
                  disabled={!leaderBulkFile || leaderBulkImporting}
                >
                  {leaderBulkImporting ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      Importing...
                    </>
                  ) : (
                    <>
                      <Upload className="h-4 w-4 mr-2" />
                      Import
                    </>
                  )}
                </Button>
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Edit Geography Modal */}
        <Dialog open={showEditGeoModal} onOpenChange={setShowEditGeoModal}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Edit className="h-5 w-5 text-blue-600" />
                Edit {editingGeoItem?.type?.charAt(0).toUpperCase() + editingGeoItem?.type?.slice(1)}
              </DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              <div>
                <Label htmlFor="geo-name">Name</Label>
                <Input
                  id="geo-name"
                  value={geoEditForm.name}
                  onChange={(e) => setGeoEditForm({ ...geoEditForm, name: e.target.value })}
                  className="mt-1"
                />
              </div>
              
              <div className="flex items-center justify-between">
                <Label htmlFor="geo-enabled">Enabled</Label>
                <button
                  type="button"
                  onClick={() => setGeoEditForm({ ...geoEditForm, is_enabled: !geoEditForm.is_enabled })}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    geoEditForm.is_enabled ? 'bg-blue-600' : 'bg-gray-200'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      geoEditForm.is_enabled ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
              
              <p className="text-sm text-gray-500">
                {geoEditForm.is_enabled 
                  ? 'This location will appear in dropdowns.'
                  : 'This location will be hidden from dropdowns.'}
              </p>
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowEditGeoModal(false)}>
                Cancel
              </Button>
              <Button onClick={handleUpdateGeoItem}>
                Save Changes
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Add District Modal */}
        <Dialog open={showAddDistrictModal} onOpenChange={setShowAddDistrictModal}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Plus className="h-5 w-5 text-green-600" />
                Add New District
              </DialogTitle>
              <p className="text-sm text-gray-500 mt-1">
                Adding to: {geoStates.find(s => s.state_id === selectedGeoState)?.name}
              </p>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              <div>
                <Label htmlFor="district-name">District Name *</Label>
                <Input
                  id="district-name"
                  value={addDistrictForm.name}
                  onChange={(e) => setAddDistrictForm({ ...addDistrictForm, name: e.target.value })}
                  placeholder="Enter district name"
                  className="mt-1"
                />
              </div>
              
              <div>
                <Label htmlFor="district-code">District Code (optional)</Label>
                <Input
                  id="district-code"
                  value={addDistrictForm.code}
                  onChange={(e) => setAddDistrictForm({ ...addDistrictForm, code: e.target.value.toUpperCase() })}
                  placeholder="e.g., KAM"
                  maxLength={5}
                  className="mt-1"
                />
                <p className="text-xs text-gray-500 mt-1">Short code for the district (auto-generated if empty)</p>
              </div>
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => {
                setShowAddDistrictModal(false);
                setAddDistrictForm({ name: '', code: '' });
              }}>
                Cancel
              </Button>
              <Button onClick={handleAddDistrict} className="bg-green-600 hover:bg-green-700">
                Add District
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Add Constituency Modal */}
        <Dialog open={showAddConstituencyModal} onOpenChange={setShowAddConstituencyModal}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Plus className="h-5 w-5 text-green-600" />
                Add New Constituency
              </DialogTitle>
              <p className="text-sm text-gray-500 mt-1">
                Adding to: {geoDistricts.find(d => d.district_id === selectedGeoDistrict)?.name}
              </p>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              <div>
                <Label htmlFor="const-name">Constituency Name *</Label>
                <Input
                  id="const-name"
                  value={addConstituencyForm.name}
                  onChange={(e) => setAddConstituencyForm({ ...addConstituencyForm, name: e.target.value })}
                  placeholder="Enter constituency name"
                  className="mt-1"
                />
              </div>
              
              <div>
                <Label htmlFor="const-number">Constituency Number (optional)</Label>
                <Input
                  id="const-number"
                  type="number"
                  value={addConstituencyForm.number}
                  onChange={(e) => setAddConstituencyForm({ ...addConstituencyForm, number: e.target.value })}
                  placeholder="e.g., 45"
                  className="mt-1"
                />
              </div>
              
              <div>
                <Label>Reserved Category</Label>
                <Select 
                  value={addConstituencyForm.reserved_category} 
                  onValueChange={(v) => setAddConstituencyForm({ ...addConstituencyForm, reserved_category: v })}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="None (General)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None (General)</SelectItem>
                    <SelectItem value="SC">SC (Scheduled Caste)</SelectItem>
                    <SelectItem value="ST">ST (Scheduled Tribe)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label>Type</Label>
                <Select 
                  value={addConstituencyForm.type} 
                  onValueChange={(v) => setAddConstituencyForm({ ...addConstituencyForm, type: v })}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Assembly" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="assembly">Assembly (State)</SelectItem>
                    <SelectItem value="parliamentary">Parliamentary (Lok Sabha)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => {
                setShowAddConstituencyModal(false);
                setAddConstituencyForm({ name: '', number: '', reserved_category: '', type: 'assembly' });
              }}>
                Cancel
              </Button>
              <Button onClick={handleAddConstituency} className="bg-green-600 hover:bg-green-700">
                Add Constituency
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Geography Confirmation Modal */}
        <Dialog open={showDeleteGeoModal} onOpenChange={setShowDeleteGeoModal}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-red-600">
                <Trash2 className="h-5 w-5" />
                Confirm Delete
              </DialogTitle>
            </DialogHeader>
            
            <div className="py-4">
              <p className="text-gray-700">
                Are you sure you want to delete <strong>{deletingGeoItem?.name}</strong>?
              </p>
              {deletingGeoItem?.type === 'district' && (
                <p className="text-red-600 text-sm mt-2">
                  Warning: This will also delete all constituencies within this district.
                </p>
              )}
              <p className="text-gray-500 text-sm mt-2">
                This action cannot be undone.
              </p>
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => {
                setShowDeleteGeoModal(false);
                setDeletingGeoItem(null);
              }}>
                Cancel
              </Button>
              <Button 
                onClick={handleDeleteGeoItem} 
                className="bg-red-600 hover:bg-red-700"
              >
                Delete
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Create Ad Modal */}
        <Dialog open={showAddAdModal} onOpenChange={setShowAddAdModal}>
          <DialogContent className="sm:max-w-md max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Megaphone className="h-5 w-5 text-purple-600" />
                Create New Ad
              </DialogTitle>
            </DialogHeader>
            
            <form onSubmit={handleCreateAd} className="space-y-3 py-2">
              <div className="space-y-1">
                <Label htmlFor="ad-title">Title *</Label>
                <input
                  id="ad-title"
                  type="text"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                  placeholder="Enter ad title"
                  required
                />
              </div>
              
              <div className="space-y-1">
                <Label htmlFor="ad-description">Description</Label>
                <textarea
                  id="ad-description"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 resize-none"
                  placeholder="Enter ad description (optional)"
                  rows={2}
                />
              </div>
              
              <div className="space-y-1">
                <Label htmlFor="ad-link">Link (optional)</Label>
                <input
                  id="ad-link"
                  type="url"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                  placeholder="https://example.com"
                />
              </div>
              
              <div className="space-y-1">
                <Label htmlFor="ad-image">Ad Image</Label>
                <label className="block">
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-3 text-center cursor-pointer hover:border-purple-400 transition-colors">
                    {adImagePreview ? (
                      <img
                        src={adImagePreview}
                        alt="Preview"
                        className="max-h-20 mx-auto rounded"
                      />
                    ) : (
                      <>
                        <Upload className="h-6 w-6 mx-auto text-gray-400 mb-1" />
                        <p className="text-xs text-gray-500">Click to upload</p>
                      </>
                    )}
                  </div>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleAdImageChange}
                    className="hidden"
                  />
                </label>
              </div>
              
              <div className="flex items-center gap-3">
                <Label className="text-sm">Active</Label>
                <button
                  type="button"
                  onClick={() => setAdIsActive(prev => !prev)}
                  className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                    adIsActive ? 'bg-purple-600' : 'bg-gray-200'
                  }`}
                >
                  <span
                    className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${
                      adIsActive ? 'translate-x-5' : 'translate-x-1'
                    }`}
                  />
                </button>
                <span className="text-xs text-gray-500">
                  {adIsActive ? 'Visible' : 'Hidden'}
                </span>
              </div>
              
              <DialogFooter className="pt-2 gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setShowAddAdModal(false);
                    resetAdForm();
                  }}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  size="sm"
                  disabled={adSubmitting}
                  className="bg-purple-600 hover:bg-purple-700"
                >
                  {adSubmitting ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                      Creating...
                    </>
                  ) : (
                    'Create Ad'
                  )}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Edit Ad Modal */}
        <Dialog open={showEditAdModal} onOpenChange={(open) => {
          setShowEditAdModal(open);
          if (!open) {
            setEditingAd(null);
            resetAdForm();
          }
        }}>
          <DialogContent className="sm:max-w-md max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Edit className="h-5 w-5 text-blue-600" />
                Edit Ad
              </DialogTitle>
            </DialogHeader>
            
            {editingAd && (
              <form onSubmit={handleEditAd} className="space-y-3 py-2">
                <div className="space-y-1">
                  <Label htmlFor="edit-ad-title">Title *</Label>
                  <input
                    id="edit-ad-title"
                    type="text"
                    defaultValue={editingAd.title}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Enter ad title"
                    required
                  />
                </div>
                
                <div className="space-y-1">
                  <Label htmlFor="edit-ad-description">Description</Label>
                  <textarea
                    id="edit-ad-description"
                    defaultValue={editingAd.description || ''}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                    placeholder="Enter ad description (optional)"
                    rows={2}
                  />
                </div>
                
                <div className="space-y-1">
                  <Label htmlFor="edit-ad-link">Link (optional)</Label>
                  <input
                    id="edit-ad-link"
                    type="url"
                    defaultValue={editingAd.link || ''}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="https://example.com"
                  />
                </div>
                
                <div className="space-y-1">
                  <Label>Ad Image</Label>
                  <label className="block">
                    <div className="border-2 border-dashed border-gray-300 rounded-lg p-3 text-center cursor-pointer hover:border-blue-400 transition-colors">
                      {adImagePreview ? (
                        <img
                          src={adImagePreview}
                          alt="Preview"
                          className="max-h-20 mx-auto rounded"
                        />
                      ) : (
                        <>
                          <Upload className="h-6 w-6 mx-auto text-gray-400 mb-1" />
                          <p className="text-xs text-gray-500">Click to upload</p>
                        </>
                      )}
                    </div>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleAdImageChange}
                      className="hidden"
                    />
                  </label>
                </div>
                
                <div className="flex items-center gap-3">
                  <Label className="text-sm">Active</Label>
                  <button
                    type="button"
                    onClick={() => setAdIsActive(prev => !prev)}
                    className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                      adIsActive ? 'bg-blue-600' : 'bg-gray-200'
                    }`}
                  >
                    <span
                      className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${
                        adIsActive ? 'translate-x-5' : 'translate-x-1'
                      }`}
                    />
                  </button>
                  <span className="text-xs text-gray-500">
                    {adIsActive ? 'Visible' : 'Hidden'}
                  </span>
                </div>
                
                <DialogFooter className="pt-2 gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setShowEditAdModal(false);
                      setEditingAd(null);
                      resetAdForm();
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    size="sm"
                    disabled={adSubmitting}
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    {adSubmitting ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      'Save Changes'
                    )}
                  </Button>
                </DialogFooter>
              </form>
            )}
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog open={!!deleteConfirm} onOpenChange={(open) => !open && setDeleteConfirm(null)}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-red-600">
                <Trash2 className="h-5 w-5" />
                Confirm Deletion
              </DialogTitle>
            </DialogHeader>
            <div className="py-4">
              <p className="text-gray-700">
                Are you sure you want to delete <strong>{deleteConfirm?.name}</strong>?
              </p>
              <p className="text-sm text-gray-500 mt-2">
                This action cannot be undone.
              </p>
            </div>
            <DialogFooter className="gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setDeleteConfirm(null)}
                disabled={deletingId}
              >
                Cancel
              </Button>
              <Button
                type="button"
                variant="destructive"
                disabled={deletingId}
                onClick={() => {
                  if (deleteConfirm?.type === 'leader') {
                    handleDeleteLeader(deleteConfirm.id);
                  } else if (deleteConfirm?.type === 'issue') {
                    handleDeleteIssue(deleteConfirm.id);
                  } else if (deleteConfirm?.type === 'response') {
                    handleDeleteResponse(deleteConfirm.id);
                  } else if (deleteConfirm?.type === 'room') {
                    handleDeleteRoom(deleteConfirm.id);
                  }
                }}
              >
                {deletingId ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                    Deleting...
                  </>
                ) : (
                  'Delete'
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default AdminDashboard;
